import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ClipboardCheck, ClipboardList, ScanLine, Loader2, ShieldAlert, LogOut,
  Warehouse, Package, MapPin, CheckCircle2, RefreshCw, ArrowRight, UserCheck,
  Truck, Camera, Images, X, UserPlus, Search, ChevronDown, Boxes, Shield, PackagePlus,
} from 'lucide-react';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { formatDate, formatDateTime } from '../lib/dateFormat';
import WarehouseTaskScanner from '../components/WarehouseTaskScanner';
import { WarehouseFilterSelect, useWarehouseFilter } from '../components/WarehouseFilterSelect';
import BarcodeScannerModal from '../components/BarcodeScannerModal';
import PickQtyModal from '../components/PickQtyModal';
import FloorStockCheckPanel from '../components/FloorStockCheckPanel';
import FloorAuditPanel from '../components/FloorAuditPanel';
import NotificationsBell from '../components/NotificationsBell';
import FloorAssignPicker from '../components/FloorAssignPicker';
import LoadingPicturesSection from '../components/LoadingPicturesSection';
import SyncStatusBadge from '../components/SyncStatusBadge';
import { orderPartyPrimary } from '../lib/orderParty';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

/**
 * FloorTasks — mobile-first warehouse floor operations page.
 *
 * Design constraints:
 *  * Full-screen (no admin sidebar) so one-handed thumb reach hits every
 *    interactive element on a common 6" Android phone.
 *  * 48px+ tap targets on every button (WCAG AAA touch target size).
 *  * Card-stack layout for tasks (no horizontal scroll, minimum vertical
 *    scrolling; each card fits key info in a single glance).
 *  * Filter chips pill-shaped, 44px+ tall, use full row width.
 *  * Confirmation via `AlertDialog` (Radix — accessible + can't be
 *    dismissed by accidental backdrop taps thanks to its focus trap).
 *  * Scanner reused from `WarehouseTaskScanner.jsx`; the underlying ZXing
 *    reader is dynamically imported so the initial paint is fast even
 *    on low-end Androids (~1 MB less JS parsed on first load).
 *
 * RBAC — enforced client-side for UX; the backend `_require_role` is
 * still the authoritative gate:
 *  * `manager` → sees Picking + Put-away + Transfers tab
 *  * `floor_incharge` → Put-away + Transfers, Picking read-only
 *  * `picker` → Picking only
 *  * `viewer` → read-only across everything
 *  * no role → full-screen access-denied panel with sign-out CTA.
 */

const FILTER_CHIPS = [
  { key: '', label: 'Open', match: (t) => t.status !== 'completed' },
  { key: 'pending', label: 'Pending', match: (t) => t.status === 'pending' },
  { key: 'in_progress', label: 'In progress', match: (t) => t.status === 'in_progress' },
  { key: 'completed', label: 'Done today', match: (t) => t.status === 'completed' },
];

const TASK_TYPE_TABS = [
  { key: 'picking',  label: 'Picking',   icon: ClipboardCheck, color: 'amber',
    caps: ['picker', 'manager', 'floor_incharge', 'viewer'] },
  { key: 'putaway',  label: 'Put-away',  icon: ClipboardList,  color: 'indigo',
    caps: ['manager', 'floor_incharge', 'viewer'] },
  // Feb 2026 loading-task feature — floor-in-charge captures goods +
  // seal photos to close out a Loading task. Reviewer role still sees
  // the tab so managers can audit completed loadings.
  { key: 'loading',  label: 'Loading',   icon: Truck,          color: 'emerald',
    caps: ['manager', 'floor_incharge', 'viewer'] },
  // Feb 2026 v3 — Receiving mode. The assigned floor user scans the
  // ASN number, enters actual prod/exp dates + received qty, and
  // hits Confirm — spawns Stage placements + put-away tasks with
  // variance logged against the ASN.
  { key: 'receiving', label: 'Receiving', icon: PackagePlus,   color: 'blue',
    caps: ['manager', 'floor_incharge'] },
  // Feb 2026 Pallet Returns tab — dock-side floor user (with the
  // explicit `allow_pallet_returns` flag) confirms branch-initiated
  // physical pallet returns.
  { key: 'pallet_returns',  label: 'Pallet Returns',  icon: Boxes,  color: 'indigo',
    caps: [] },
  // Feb 2026 — Stock Check tab. Read-only lookup for the floor —
  // scans Pallet IDs / SKUs / Location IDs and renders a mobile card
  // stack with product, on-hand / available / allocated, location
  // and batch info. Gated by super-admin OR the per-user
  // `allow_stock_check` toggle OR manager/floor_incharge role.
  { key: 'stock_check', label: 'Stock Check', icon: Search, color: 'teal',
    caps: [] },
  // Feb 2026 — Audit tab. Permitted users (`allow_floor_audit`)
  // search any pallet / location / product and submit a Request
  // Move to the super-admin queue; on approval the pallet moves
  // via the same transfer helper the manager-initiated transfer
  // uses, so inventory + bin occupancy + ledger all update together.
  { key: 'audit', label: 'Audit', icon: ClipboardCheck, color: 'amber',
    caps: [] },
];

const ROLE_DESC = {
  manager: 'Warehouse Manager',
  floor_incharge: 'Floor In-charge',
  picker: 'Picker',
  viewer: 'Viewer',
};


const FloorTasks = () => {
  const { token, authHeaders, logout, email } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [me, setMe] = useState(null);
  const [tasks, setTasks] = useState([]);
  // Feb 2026 — Top-of-page warehouse filter. Renders only when the
  // caller has >1 accessible warehouse; the selection is persisted
  // in localStorage so pickers keep the same site across reloads.
  const {
    warehouses: whList,
    value: whFilter,
    setValue: setWhFilter,
  } = useWarehouseFilter('floor-tasks');
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [meLoading, setMeLoading] = useState(true);
  const [type, setType] = useState('picking');
  const [status, setStatus] = useState('');
  // Feb 2026 — Floor Mode selector switched from a horizontal pill row
  // to a searchable dropdown so it scales cleanly on narrow phone
  // screens even when new modes get added (e.g. Pallet Returns pushed
  // the row to two-line wraps). `modePickerOpen` controls the Popover;
  // `modeQuery` filters the list by label match.
  const [modePickerOpen, setModePickerOpen] = useState(false);
  const [modeQuery, setModeQuery] = useState('');
  const [scanTask, setScanTask] = useState(null);
  // Feb 2026 (picker qty entry): picking tasks open the PickQtyModal
  // instead of the plain confirm dialog so the picker can enter the
  // actual quantity taken from the pallet. Put-away tasks keep the
  // legacy two-tap confirm.
  const [pickTask, setPickTask] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { task, nextStatus, label }
  // Feb 2026 — Zone Management dropdown for the Floor Put-Away
  // completion dialog. Loaded once from the master `zones` collection
  // (falls back to an empty list on any error — the field stays
  // optional, so operators can still hit Complete without picking).
  const [zoneOptions, setZoneOptions] = useState([]);
  const [confirmZone, setConfirmZone] = useState("");
  // Feb 2026 — Hide the zone field entirely for warehouses that have
  // no zones configured. Map: `{ [warehouse_id]: boolean }` cached
  // once per session. `true` = at least one zone exists for that
  // warehouse, `false` = none, `undefined` = not yet probed. We keep
  // the lookup optimistic (default behaviour = show zone chip until
  // we know otherwise) so the picker is never blocked waiting on
  // this background probe.
  const [warehouseHasZones, setWarehouseHasZones] = useState({});
  const [assignTask, setAssignTask] = useState(null);
  // Feb 2026 — order selector at the top of the floor page. Lists
  // every distinct order (across all task types + all statuses)
  // assigned to the current user, powered by
  // `GET /admin/warehouse/my-orders`. Selecting an order filters the
  // task cards below to that single order so the picker can walk one
  // order at a time. `selectedOrderId === ''` renders every task.
  const [myOrders, setMyOrders] = useState([]);
  const [myOrdersLoading, setMyOrdersLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  // Feb 2026 — searchable dropdown state. `orderPickerOpen` toggles
  // the Popover; `orderQuery` filters the list by order number,
  // branch name, or customer name (matches `label` fallback chain).
  const [orderPickerOpen, setOrderPickerOpen] = useState(false);
  const [orderQuery, setOrderQuery] = useState('');

  // Redirect to login if token missing — preserving the intended
  // destination so the user lands back on the floor page after sign in.
  // We route through `/warehouse/login` (not `/admin/login`) so the
  // browser URL never leaves the `/warehouse` scope during sign-in —
  // this keeps the Warehouse PWA manifest active throughout the login
  // roundtrip and prevents Chrome/Android from dropping the standalone
  // shell (or, on iOS, redirecting through the storefront shell).
  // We also delay the redirect a tick so shared /warehouse/floor URLs
  // render a friendly "signing you in" screen instead of a jarring
  // hard navigation, which matters when the URL is texted to staff.
  useEffect(() => {
    if (!token) {
      const dest = `${location.pathname}${location.search || ''}`;
      const next = encodeURIComponent(dest || '/warehouse/floor');
      const t = setTimeout(() => {
        navigate(`/warehouse/login?next=${next}`, { replace: true });
      }, 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [token, navigate, location.pathname, location.search]);

  // Load role context so we can gate the UI.
  const loadMe = async () => {
    setMeLoading(true);
    try {
      const r = await axios.get(`${API}/admin/warehouses/me`, { headers: authHeaders });
      setMe(r.data);
    } catch (e) {
      setMe(null);
    } finally {
      setMeLoading(false);
    }
  };
  useEffect(() => { if (token) loadMe(); /* eslint-disable-next-line */ }, [token]);

  const roles = useMemo(() => {
    if (!me) return [];
    if (me.is_super_admin) return ['manager'];
    return Array.isArray(me.roles) && me.roles.length ? me.roles : (me.role ? [me.role] : []);
  }, [me]);

  // Feb 2026 — Per-user Floor-Mode toggles (`allow_floor_*`) extend
  // the role-based gates so super admin can grant these tabs to any
  // user (e.g. a picker or an unroled ops sub-admin). Super admin
  // always sees everything.
  const flagReceiving = !!(me && (me.is_super_admin || me.allow_floor_receiving));
  const flagPutaway = !!(me && (me.is_super_admin || me.allow_floor_putaway));
  const flagLoading = !!(me && (me.is_super_admin || me.allow_floor_loading));

  const canRead = roles.length > 0 || flagReceiving || flagPutaway || flagLoading;
  const has = (r) => roles.includes(r);
  // Capability matrix — which task types the user may act on.
  const canPick = has('manager') || has('picker');
  const canPutaway = has('manager') || has('floor_incharge') || flagPutaway;  // Loading tasks are floor-in-charge (or manager) only — same as
  // put-away but the completion flow uploads goods + seal photos.
  const canLoad = has('manager') || has('floor_incharge') || flagLoading;
  // Feb 2026 v3 — Receiving mode: manager or floor_incharge can scan
  // an ASN, enter actuals, and confirm receipt from the floor. Also
  // gated on by the per-user `allow_floor_receiving` flag.
  const canReceiving = has('manager') || has('floor_incharge') || flagReceiving;
  // Feb 2026 — Pallet Returns is gated by the explicit
  // `allow_pallet_returns` flag on the admin_users row (super admin
  // flips it on). Independent of warehouse role — a picker at the
  // dock can hold it just as easily as a floor-incharge.
  const canPalletReturns = !!(me && (me.is_super_admin || me.allow_pallet_returns));
  // Feb 2026 — Stock Check gate. Server-resolved `can_stock_check`
  // is the single source of truth — True when the caller is super
  // admin OR has the `allow_stock_check` user toggle OR has been
  // granted the `warehouse.stock_check` permission OR holds any
  // warehouse role (manager / floor_incharge / picker / viewer).
  // Falls back to the legacy client-side gate if the server response
  // hasn't been rebuilt yet (deploy-window safety).
  const canStockCheck = !!(me && (
    me.can_stock_check
    || me.is_super_admin
    || me.allow_stock_check
    || (Array.isArray(me.permissions) && (
      me.permissions.includes('warehouse.stock_check')
      || me.permissions.includes('warehouse')
    ))
    || roles.length > 0
  ));
  // Feb 2026 — Audit gate. Super admin OR per-user
  // `allow_floor_audit` toggle. Independent of Stock Check — a user
  // can hold Audit without Stock Check (search inside the Audit
  // tab is still allowed because the Stock Check endpoint also
  // honours the audit flag).
  const canAudit = !!(me && (me.is_super_admin || me.allow_floor_audit));
  // Viewer is read-only across everything (canRead=true, canAct=false).
  const canAct = has('manager') || has('floor_incharge') || has('picker') || flagReceiving || flagPutaway || flagLoading;
  // Feb 2026 — per-user "Assign tasks from Floor page" gate. Super
  // admin flips this on for a Floor In-charge (or any trusted lead)
  // in Admin › Users. Not tied to warehouse role — a picker with the
  // toggle would ALSO see the button (unusual but supported).
  const canAssign = !!(me && (me.is_super_admin || me.allow_floor_task_assignment));

  // Pick a default tab that the user actually has access to.
  useEffect(() => {
    if (!canRead) return;
    if (type === 'picking' && !canPick && canPutaway) setType('putaway');
    if (type === 'putaway' && !canPutaway && canPick) setType('picking');
    // Feb 2026 — Fallback for users who only hold a per-user Floor
    // flag (no warehouse role). If the currently-selected tab isn't
    // permitted for them, jump to the first tab they DO have access
    // to so they don't land on an unusable empty state.
    const tabAllowed = (
      (type === 'picking' && (canPick || has('viewer'))) ||
      (type === 'putaway' && (canPutaway || has('viewer'))) ||
      (type === 'loading' && (canLoad || has('viewer'))) ||
      (type === 'receiving' && canReceiving) ||
      (type === 'pallet_returns' && canPalletReturns) ||
      (type === 'stock_check' && canStockCheck) ||
      (type === 'audit' && canAudit)
    );
    if (!tabAllowed) {
      if (canReceiving) setType('receiving');
      else if (canPutaway) setType('putaway');
      else if (canLoad) setType('loading');
      else if (canPick) setType('picking');
      else if (canPalletReturns) setType('pallet_returns');
      else if (canStockCheck) setType('stock_check');
      else if (canAudit) setType('audit');
    }
    // eslint-disable-next-line
  }, [canPick, canPutaway, canLoad, canReceiving, canPalletReturns, canStockCheck, canAudit, canRead]);

  // Load tasks — filtered server-side by type; we do status filtering
  // client-side to keep the UI snappy on a slow connection.
  const load = async (opts = {}) => {
    if (!canRead) return;
    // Feb 2026 v3 — Receiving mode does not use the warehouse tasks
    // API; the ASN receive endpoint is called directly by the
    // Receiving UI below. Skip the fetch so a `type=receiving`
    // request doesn't 400 on an invalid task-type.
    if (type === 'receiving') { setTasks([]); setLoading(false); setReloading(false); return; }
    // Feb 2026 — Stock Check is read-only search; no task-list fetch.
    if (type === 'stock_check') { setTasks([]); setLoading(false); setReloading(false); return; }
    if (opts.silent) setReloading(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('type', type);
      if (whFilter) params.set('warehouse_id', whFilter);
      const r = await axios.get(`${API}/admin/warehouse/tasks?${params.toString()}`, { headers: authHeaders });
      setTasks(r.data.tasks || []);
    } catch (e) {
      toast({ title: 'Failed to load tasks', description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false); setReloading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [type, canRead, whFilter]);

  // Feb 2026 — Probe each unique warehouse touched by the current
  // task list for configured zones. We fire one GET per unseen
  // warehouse_id, flip `warehouseHasZones[wh]` to true/false, and
  // keep the result for the rest of the session. This drives the
  // zone chip + put-away completion zone dropdown — both are hidden
  // when the probe comes back empty. Errors leave the entry
  // undefined so the UI falls back to its optimistic default
  // (show zone) rather than silently stripping it on a flaky
  // network blip.
  useEffect(() => {
    if (!canRead) return;
    const unseen = Array.from(
      new Set(
        (tasks || [])
          .map((t) => t?.warehouse_id)
          .filter((wid) => wid && !(wid in warehouseHasZones)),
      ),
    );
    if (unseen.length === 0) return;
    unseen.forEach((wid) => {
      axios
        .get(`${API}/admin/warehouse/zones`, {
          params: { warehouse_id: wid },
          headers: authHeaders,
        })
        .then((r) => {
          const hasAny = Array.isArray(r.data?.zones) && r.data.zones.length > 0;
          setWarehouseHasZones((prev) => ({ ...prev, [wid]: hasAny }));
        })
        .catch(() => {
          // Leave the entry undefined — UI stays optimistic.
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, canRead]);
  // Auto-refresh every 20s so pickers see task changes quickly
  // (Feb 2026 — was 45s, tightened after unassigned tasks lingered
  // on the old assignee's screen for too long).
  useEffect(() => {
    if (!canRead) return () => {};
    const id = setInterval(() => load({ silent: true }), 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [canRead, type]);

  // 2026-10 — Live SSE channel. Reassignments + pick-confirm flips
  // publish to the picker's per-user floor channel on the backend;
  // we re-fetch the task list the moment one lands instead of
  // waiting up to 20s for the next poll. Browser EventSource can't
  // set custom headers, so we pass the JWT as a `?token=` query
  // param — the backend `require_admin` dep already accepts that
  // fallback for GET streams. The 20s poll remains the safety net
  // for dropped events or pod restarts.
  useEffect(() => {
    if (!canRead) return () => {};
    const tok = localStorage.getItem('admin_token')
      || localStorage.getItem('token')
      || '';
    if (!tok) return () => {};
    const url = `${API}/warehouse/floor/stream?token=${encodeURIComponent(tok)}`;
    let es;
    let retryTimer;
    const refresh = () => {
      // Both the task list and the my-orders dropdown may have
      // changed (new order appeared / disappeared) so refresh
      // both silently.
      load({ silent: true });
      loadMyOrders();
    };
    const connect = () => {
      try {
        es = new EventSource(url, { withCredentials: false });
        es.addEventListener('ready', () => { /* no-op */ });
        es.addEventListener('task.reassigned', refresh);
        es.addEventListener('task.updated', refresh);
        es.onerror = () => {
          // Browser auto-reconnects, but if the server closed with
          // a non-200 the EventSource stays in CLOSED. Nudge a
          // reconnect after 5s so a transient 502 doesn't kill the
          // live feed permanently.
          try { es.close(); } catch { /* ignore */ }
          retryTimer = setTimeout(connect, 5000);
        };
      } catch {
        retryTimer = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      try { es && es.close(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, type]);

  // Feb 2026 — fetch the "my orders" list that powers the order
  // selector dropdown in the header. Refreshed on the same 20s
  // cadence as the task list so new task assignments surface in the
  // dropdown without a manual reload. The user MUST explicitly pick
  // an order before the task list renders — we never auto-select on
  // load (was a Feb 2026 refinement) so the picker/manager sees the
  // "Select an order" prompt and consciously enters an order
  // context. If a previously-selected order disappears (task moved,
  // order completed, etc.), we clear the selection back to `''` so
  // the prompt reappears.
  const loadMyOrders = async () => {
    if (!canRead) return;
    try {
      const r = await axios.get(`${API}/admin/warehouse/my-orders`, { headers: authHeaders });
      const list = r.data?.orders || [];
      setMyOrders(list);
      setSelectedOrderId((prev) => {
        if (prev && list.some((o) => o.order_id === prev)) return prev;
        // Do NOT auto-pick list[0] — force an explicit choice.
        return '';
      });
    } catch (_e) {
      // Non-fatal — the dropdown just stays empty and the filter
      // effectively becomes a no-op.
    } finally {
      setMyOrdersLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMyOrders(); }, [canRead]);
  useEffect(() => {
    if (!canRead) return () => {};
    const id = setInterval(loadMyOrders, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [canRead]);

  const filteredTasks = useMemo(() => {
    const chip = FILTER_CHIPS.find((c) => c.key === status) || FILTER_CHIPS[0];
    const byOrder = (t) => !selectedOrderId || t.order_id === selectedOrderId;
    const arr = (tasks || []).filter((t) => chip.match(t) && byOrder(t));
    // Feb 2026 — Pickers walk the warehouse in location order. Sort
    // ONLY the picking view alphabetically by `location_id` (case-
    // insensitive, blanks last). Mirrors the backend pick-list PDF
    // sort (`/admin/orders/{id}/dispatch/pick-list`) so the on-floor
    // screen and the paper the picker was handed agree row-for-row.
    // Scoped to type === 'picking' per user directive — put-away,
    // loading and pallet-return views keep their arrival order so
    // task-dispatch sequence isn't disrupted.
    if (type === 'picking') {
      arr.sort((a, b) => {
        const al = (a.location_id || '').trim().toUpperCase();
        const bl = (b.location_id || '').trim().toUpperCase();
        if (!al && bl) return 1;
        if (al && !bl) return -1;
        if (al === bl) return 0;
        return al < bl ? -1 : 1;
      });
    }
    return arr;
  }, [tasks, status, selectedOrderId, type]);

  // Feb 2026 — searchable-dropdown filter. Matches on order number,
  // branch name, and customer name (which is what the `label`
  // fallback chain surfaces on retail orders). Case-insensitive
  // substring — no fuzzy scoring since the picker is scanning for
  // an exact order they just heard from a manager or read off a
  // packing slip. Cap at 200 rows so a warehouse with hundreds of
  // open orders doesn't OOM the browser on every keystroke.
  const filteredMyOrders = useMemo(() => {
    const q = orderQuery.trim().toLowerCase();
    const list = myOrders || [];
    if (!q) return list.slice(0, 200);
    return list
      .filter((o) =>
        (o.order_number || '').toLowerCase().includes(q)
        || (o.branch_name || '').toLowerCase().includes(q)
        || (o.customer_name || '').toLowerCase().includes(q))
      .slice(0, 200);
  }, [myOrders, orderQuery]);

  const selectedOrder = useMemo(
    () => (myOrders || []).find((o) => o.order_id === selectedOrderId) || null,
    [myOrders, selectedOrderId],
  );

  const counts = useMemo(() => {
    const c = { total: tasks.length };
    FILTER_CHIPS.forEach((chip) => { c[chip.key] = tasks.filter(chip.match).length; });
    return c;
  }, [tasks]);

  const canScanTask = (t) => {
    if (t.status === 'completed') return false;
    if (t.type === 'picking') return canPick;
    if (t.type === 'putaway') return canPutaway;
    return false;
  };

  const startAction = (task, nextStatus, label) => {
    setConfirmAction({ task, nextStatus, label });
    // Re-seed the zone dropdown with whatever the task already carries,
    // so an operator finalising a Stage-generated task sees the derived
    // zone pre-selected but can override before hitting Complete.
    setConfirmZone(task?.zone || "");
    // Lazy-load zones for this task's warehouse on first open — one
    // request per session is enough for a dropdown.
    if (task?.type === "putaway" && task?.warehouse_id && zoneOptions.length === 0) {
      axios
        .get(`${API}/admin/warehouse/zones`, {
          params: { warehouse_id: task.warehouse_id },
          headers: authHeaders,
        })
        .then((r) => setZoneOptions(r.data?.zones || []))
        .catch(() => setZoneOptions([]));
    }
  };

  const runConfirmed = async () => {
    if (!confirmAction) return;
    const { task, nextStatus } = confirmAction;
    try {
      const payload = { status: nextStatus };
      // Feb 2026 — Put-away completion carries the operator's
      // selected zone through so the backend stops blanking it on
      // the underlying placement.
      if (
        task?.type === "putaway"
        && nextStatus === "completed"
        && confirmZone
      ) {
        payload.zone = confirmZone;
      }
      const r = await axios.patch(
        `${API}/admin/warehouse/tasks/${task.id}`,
        payload,
        { headers: authHeaders },
      );
      setTasks((prev) => prev.map((t) => (t.id === task.id ? r.data : t)));
      try { navigator.vibrate?.(40); } catch { /* noop */ }
      toast({ title: `Task ${nextStatus === 'completed' ? 'completed' : 'in progress'}` });
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e?.message });
    } finally {
      setConfirmAction(null);
      setConfirmZone("");
    }
  };

  // Feb 2026 — Super-admin-only "Reset Task" action. Resets a task of
  // any type and any status back to `pending`. Wraps the reset
  // endpoint in a confirm dialog so an accidental tap on a
  // completed task doesn't quietly re-open work.
  const resetTaskAsSuperAdmin = async (task) => {
    if (!window.confirm(
      `Reset this ${task.type} task back to Pending?\n\n` +
      `Current status: ${task.status || 'pending'}\n` +
      `${task.type === 'picking' ? 'Any qty already picked will be reset to 0 and the placement allocation will be restored.' : ''}`,
    )) return;
    try {
      const r = await axios.post(
        `${API}/admin/warehouse/tasks/${task.id}/reset`,
        {},
        { headers: authHeaders },
      );
      const fresh = r.data?.task;
      if (fresh) {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? fresh : t)));
      } else {
        load({ silent: true });
      }
      toast({ title: 'Task reset', description: `${task.type} task returned to Pending.` });
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  // -----------------------------------------
  // Render
  // -----------------------------------------
  // Not signed in yet — show a friendly branded splash for the ~400ms
  // before the useEffect redirects to /admin/login. This makes shared
  // /warehouse/floor URLs feel intentional even to first-time users.
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 text-center text-white" data-testid="floor-signin-prompt">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center mb-4">
          <Warehouse className="w-8 h-8 text-emerald-300" />
        </div>
        <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-bold mb-2">Warehouse floor mode</div>
        <h1 className="font-display text-2xl mb-2">Sign in to continue</h1>
        <p className="text-slate-400 text-sm max-w-xs leading-relaxed">
          This URL always opens directly onto the mobile floor operations screen. We&apos;re redirecting you to sign in with your warehouse credentials…
        </p>
        <div className="mt-6"><Loader2 className="w-6 h-6 animate-spin text-emerald-300" /></div>
      </div>
    );
  }

  if (meLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 text-center" data-testid="floor-access-denied">
        <div className="w-20 h-20 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center mb-5">
          <ShieldAlert className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-white font-display text-2xl mb-2">Access denied</h1>
        <p className="text-slate-400 text-sm max-w-xs leading-relaxed">
          You are signed in as <span className="text-white font-semibold">{email}</span>, but you don&apos;t have a warehouse role assigned.
          Ask a manager to grant you Picker, Floor In-charge, Viewer or Warehouse Manager access.
        </p>
        <button
          type="button"
          onClick={() => { logout(); navigate('/admin/login', { replace: true }); }}
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-full text-sm"
          data-testid="floor-signout-denied"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="floor-tasks-page">
      {/* Sticky header — always visible so the user can see their role +
          switch tabs without scrolling back up. */}
      <header className="sticky top-0 z-30 bg-slate-950 text-white shadow-lg">
        <div className="px-4 py-3 flex items-center gap-3">
          <Warehouse className="w-6 h-6 text-emerald-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg leading-tight">Warehouse floor</div>
            <div className="text-[11px] text-slate-400 truncate" data-testid="floor-user-info">
              {email} · {roles.map((r) => ROLE_DESC[r] || r).join(' + ')}
            </div>
            {/* Feb 2026 — Offline sync indicator. Shows Online /
                Syncing / Offline+queued / Pending. Persistent so the
                floor user knows whether their scans are landing on
                the server or waiting locally. */}
            <div className="mt-1">
              <SyncStatusBadge />
            </div>
            {/* Feb 2026 — Warehouse filter. Only renders when the
                user has >1 accessible warehouse. Persists in
                localStorage so a picker keeps the same site across
                reloads. */}
            <div className="mt-2">
              <WarehouseFilterSelect
                storageKey="floor-tasks"
                warehouses={whList}
                value={whFilter}
                onChange={setWhFilter}
                className="text-xs bg-white/10 rounded-md pl-2 pr-1"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={reloading}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center"
            aria-label="Refresh"
            data-testid="floor-refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${reloading ? 'animate-spin' : ''}`} />
          </button>
          {/* Bell — Feb 2026. Floor variant plays a driver-grade loud
              chime + shows the wider tray so workers wearing gloves
              on a noisy warehouse floor notice new task assignments
              and stage completions. */}
          <NotificationsBell role="admin" authHeaders={authHeaders} variant="floor" />
          <button
            type="button"
            onClick={() => { logout(); navigate('/admin/login', { replace: true }); }}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Sign out"
            data-testid="floor-signout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Feb 2026 — Floor Mode picker. Replaces the old horizontal
            pill row (Picking / Put-away / Loading / Pallet Returns)
            with a full-width searchable dropdown so the label reads
            cleanly on narrow phones AND scales to more modes without
            wrapping. Same RBAC gates as before — a user only sees
            entries they're allowed to work on; if they're gated to
            exactly one mode, the entire picker is hidden. */}
        {(() => {
          const allowedTabs = TASK_TYPE_TABS.filter((t) => {
            if (t.key === 'picking') return canPick || has('viewer');
            if (t.key === 'putaway') return canPutaway || has('viewer');
            if (t.key === 'loading') return canLoad || has('viewer');
            if (t.key === 'receiving') return canReceiving;
            if (t.key === 'pallet_returns') return canPalletReturns;
            if (t.key === 'stock_check') return canStockCheck;
            if (t.key === 'audit') return canAudit;
            return false;
          });
          if (allowedTabs.length <= 1) return null;
          const currentTab = allowedTabs.find((t) => t.key === type) || allowedTabs[0];
          const q = modeQuery.trim().toLowerCase();
          const filteredTabs = q
            ? allowedTabs.filter((t) => t.label.toLowerCase().includes(q))
            : allowedTabs;
          const CurrentIcon = currentTab.icon;
          return (
            <div className="px-4 pb-3" data-testid="floor-mode-picker-row">
              <label
                className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5"
                htmlFor="floor-mode-picker"
              >
                Floor Mode
              </label>
              <Popover
                open={modePickerOpen}
                onOpenChange={(open) => {
                  setModePickerOpen(open);
                  if (!open) setModeQuery('');
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    id="floor-mode-picker"
                    type="button"
                    className="w-full h-12 rounded-2xl bg-white text-slate-950 px-4 font-semibold text-sm border-2 border-transparent focus:border-emerald-400 focus:outline-none flex items-center justify-between gap-2"
                    data-testid="floor-mode-picker-trigger"
                    aria-haspopup="listbox"
                    aria-expanded={modePickerOpen}
                  >
                    <span className="inline-flex items-center gap-2 min-w-0 flex-1 text-left">
                      <CurrentIcon className="w-5 h-5 text-slate-500 shrink-0" />
                      <span className="truncate">{currentTab.label}</span>
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[440px]"
                  align="start"
                  sideOffset={6}
                  data-testid="floor-mode-picker-panel"
                >
                  <div className="border-b border-neutral-200 p-2 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400 shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      value={modeQuery}
                      onChange={(e) => setModeQuery(e.target.value)}
                      placeholder="Search mode…"
                      className="flex-1 min-w-0 outline-none text-sm bg-transparent"
                      data-testid="floor-mode-picker-search"
                    />
                    {modeQuery && (
                      <button
                        type="button"
                        onClick={() => setModeQuery('')}
                        className="text-slate-400 hover:text-slate-700 shrink-0"
                        data-testid="floor-mode-picker-clear-query"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto py-1">
                    {filteredTabs.length === 0 ? (
                      <div
                        className="px-4 py-6 text-center text-sm text-slate-500"
                        data-testid="floor-mode-picker-empty"
                      >
                        No mode matches “{modeQuery}”.
                      </div>
                    ) : (
                      filteredTabs.map((t) => {
                        const Icon = t.icon;
                        const isActive = t.key === type;
                        return (
                          <button
                            key={t.key}
                            type="button"
                            onClick={() => {
                              setType(t.key);
                              setStatus('');
                              setModePickerOpen(false);
                              setModeQuery('');
                            }}
                            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm transition ${
                              isActive
                                ? 'bg-emerald-50 text-emerald-900 font-bold'
                                : 'hover:bg-slate-50 text-slate-800'
                            }`}
                            data-testid={`floor-mode-picker-option-${t.key}`}
                          >
                            <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-emerald-600' : 'text-slate-500'}`} />
                            <span className="flex-1 truncate">{t.label}</span>
                            {isActive && (
                              <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                                Active
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          );
        })()}

        {/* Feb 2026 — Searchable order picker. Always visible so the
            picker/manager can see the selector even before my-orders
            loads. Managers/super admins see every order with any
            warehouse task; pickers see only their own assignments.
            Typing in the search input filters by order number,
            branch name, or customer name (which is what the label
            fallback chain surfaces on retail orders). Until the user
            picks an order, the task list stays behind a "Select an
            order to view tasks" prompt so a picker consciously
            enters an order context instead of blindly grabbing the
            first task at the top. */}
        {/* Feb 2026 v2 — hide the "Working on order" selector on
            Put-away, Pallet Returns, Receiving and Stock Check.
            Put-away is scoped per-task-per-assignee (there's no
            order context — the ASN already routes tasks directly to
            a picker), so surfacing an order picker on the header is
            misleading. Stock Check (Feb 2026) is a read-only
            product lookup that has no order context at all — the
            dropdown would be dead weight on a view that doesn't
            consume `selectedOrderId`. */}
        {type !== 'pallet_returns' && type !== 'putaway' && type !== 'receiving' && type !== 'stock_check' && (
        <div
          className="px-4 pb-3"
          data-testid="floor-order-selector-row"
        >
          <label
            className="block text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5"
            htmlFor="floor-order-selector"
          >
            Working on order
          </label>
          <Popover open={orderPickerOpen} onOpenChange={setOrderPickerOpen}>
            <PopoverTrigger asChild>
              <button
                id="floor-order-selector"
                type="button"
                disabled={myOrdersLoading}
                className="w-full h-12 rounded-2xl bg-white text-slate-950 px-4 font-semibold text-sm border-2 border-transparent focus:border-emerald-400 focus:outline-none disabled:opacity-60 flex items-center justify-between gap-2"
                data-testid="floor-order-selector"
                aria-haspopup="listbox"
                aria-expanded={orderPickerOpen}
              >
                <span className={`flex-1 truncate text-left ${selectedOrder ? '' : 'text-slate-400 font-normal'}`}>
                  {myOrdersLoading
                    ? 'Loading orders…'
                    : selectedOrder
                      ? selectedOrder.label
                      : myOrders.length === 0
                        ? 'No orders available'
                        : 'Select an order…'}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 w-[calc(100vw-2rem)] sm:w-[420px] max-w-[500px]"
              align="start"
              sideOffset={6}
              data-testid="floor-order-selector-panel"
            >
              <div className="border-b border-neutral-200 p-2 flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  autoFocus
                  type="text"
                  value={orderQuery}
                  onChange={(e) => setOrderQuery(e.target.value)}
                  placeholder="Search by order # or branch…"
                  className="flex-1 min-w-0 outline-none text-sm bg-transparent"
                  data-testid="floor-order-selector-search"
                />
                {orderQuery && (
                  <button
                    type="button"
                    onClick={() => setOrderQuery('')}
                    className="text-slate-400 hover:text-slate-700 shrink-0"
                    data-testid="floor-order-selector-clear-query"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="max-h-[60vh] overflow-y-auto py-1">
                {filteredMyOrders.length === 0 ? (
                  <div
                    className="px-4 py-6 text-center text-sm text-slate-500"
                    data-testid="floor-order-selector-empty"
                  >
                    {myOrders.length === 0
                      ? 'No orders available yet. New orders will appear here as they come in.'
                      : `No orders match “${orderQuery}”.`}
                  </div>
                ) : (
                  filteredMyOrders.map((o) => {
                    const isActive = o.order_id === selectedOrderId;
                    // Feb 2026 — compact "3/5" progress chip. Green
                    // when everything's done, emerald-tinted amber
                    // while in progress, slate when nothing's
                    // touched yet. `total` can be 0 briefly after
                    // an order allocation before tasks propagate —
                    // skip the chip in that case so the row stays
                    // clean.
                    const total = Number(o.total_tasks || 0);
                    const done = Number(o.completed_tasks || 0);
                    let chipCls = 'bg-slate-100 text-slate-600';
                    if (total > 0 && done === total) {
                      chipCls = 'bg-emerald-100 text-emerald-800';
                    } else if (total > 0 && done > 0) {
                      chipCls = 'bg-amber-100 text-amber-800';
                    }
                    return (
                      <button
                        key={o.order_id}
                        type="button"
                        onClick={() => {
                          setSelectedOrderId(o.order_id);
                          setOrderPickerOpen(false);
                          setOrderQuery('');
                        }}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isActive ? 'bg-emerald-50 text-emerald-900 font-semibold' : 'hover:bg-slate-50'}`}
                        data-testid={`floor-order-option-${o.order_id}`}
                      >
                        <span className="flex-1 min-w-0 truncate">
                          <span className="font-mono text-[11px] text-slate-500 mr-1.5">
                            {o.order_number}
                          </span>
                          {(o.branch_name || orderPartyPrimary(o) || 'Direct')}
                        </span>
                        {total > 0 && (
                          <span
                            className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full shrink-0 ${chipCls}`}
                            data-testid={`floor-order-progress-${o.order_id}`}
                            title={`${done} of ${total} tasks completed`}
                          >
                            {done}/{total}
                          </span>
                        )}
                        {isActive && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        )}
      </header>

      {/* Feb 2026 — Pallet Returns tab replaces the normal task list. */}
      {type === 'pallet_returns' ? (
        <PalletReturnsFloorTab authHeaders={authHeaders} />
      ) : type === 'receiving' ? (
        // Feb 2026 v3 — Floor-Mode Receiving. Bespoke UI that scans
        // the ASN number and lets the assignee enter actuals per
        // line. Falls back to a plain text input so any device with
        // a keyboard can drive it (barcode scanners default to
        // keyboard-emulation input).
        <ReceivingFloorTab authHeaders={authHeaders} toast={toast} />
      ) : type === 'stock_check' ? (
        // Feb 2026 — Read-only Stock Check search for the floor.
        // Debounced pallet/SKU/location lookup, mobile-optimised cards.
        <FloorStockCheckPanel
          API={API}
          authHeaders={authHeaders}
          warehouseId={whFilter}
        />
      ) : type === 'audit' ? (
        // Feb 2026 — Audit tab. Search any pallet / location / product
        // then submit a Request Move for super-admin review.
        <FloorAuditPanel
          API={API}
          authHeaders={authHeaders}
          warehouseId={whFilter}
          warehouses={Array.isArray(me?.warehouses) ? me.warehouses : whList}
        />
      ) : (
      <>
      {/* Filter chip row */}
      <div className="px-3 pt-3 pb-2 flex gap-2 overflow-x-auto" data-testid="floor-filter-row">
        {FILTER_CHIPS.map((chip) => {
          const active = status === chip.key;
          const count = counts[chip.key] || 0;
          return (
            <button
              key={chip.key || 'open'}
              type="button"
              onClick={() => setStatus(chip.key)}
              className={`h-11 px-4 rounded-full font-bold text-sm whitespace-nowrap inline-flex items-center gap-1.5 border-2 transition-all ${
                active
                  ? 'bg-slate-950 text-white border-slate-950'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`floor-filter-${chip.key || 'open'}`}
            >
              {chip.label}
              {count > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Task cards */}
      <div className="px-3 pb-24 space-y-3" data-testid="floor-task-list">
        {loading ? (
          <div className="py-24 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (!selectedOrderId && type !== 'putaway') ? (
          /* Feb 2026 — no order selected. The task list stays hidden
             behind a "Select an order" prompt so the picker/manager
             consciously enters an order context first. Distinct from
             the "All caught up" state (which fires AFTER a selection
             matched zero tasks).

             Feb 2026 v2 — Put-away mode bypasses this prompt entirely.
             Put-away tasks are ASN-driven, not order-driven; the
             assignee opens Floor → Put-away and immediately sees the
             tasks they were routed. */
          <div className="mt-16 text-center px-6" data-testid="floor-select-order-prompt">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <ClipboardList className="w-8 h-8 text-slate-500" />
            </div>
            <div className="text-slate-700 font-bold text-lg">Select an order to view tasks</div>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              {myOrdersLoading
                ? 'Loading your orders…'
                : myOrders.length === 0
                  ? "You don't have any orders yet. This page will refresh automatically as new work comes in."
                  : 'Use the dropdown above to pick an order — the tasks for that order will appear here.'}
            </p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="mt-16 text-center px-6" data-testid="floor-tasks-empty">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="text-slate-700 font-bold text-lg">All caught up</div>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              No {type === 'picking' ? 'picking' : type === 'loading' ? 'loading' : 'put-away'} tasks match this filter right now. Pull to refresh.
            </p>
          </div>
        ) : (
          filteredTasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              canScan={canScanTask(t)}
              canAct={canAct}
              canAssign={canAssign}
              isSuperAdmin={!!me?.is_super_admin}
              canDeleteLoadingPhotos={!!me?.is_super_admin || !!me?.allow_delete_loading_photos}
              warehouseHasZones={warehouseHasZones}
              onReset={() => resetTaskAsSuperAdmin(t)}
              onAssign={() => setAssignTask(t)}
              onScan={() => setScanTask(t)}
              onStart={() => startAction(t, 'in_progress', 'Start task')}
              onLoadingUploaded={(updated) => {
                // Refresh the task list so the just-completed loading
                // task drops off the pending queue.
                load({ silent: true });
                toast({
                  title: 'Loading complete',
                  description: `${updated.photo_count} photo${updated.photo_count === 1 ? '' : 's'} uploaded · admin notified.`,
                });
              }}
              onComplete={() => {
                // Picking tasks route through the qty-entry modal so
                // the picker can enter the actual quantity + trigger
                // FEFO shortfall cascade. Put-away keeps the legacy
                // two-tap confirm dialog.
                if (t.type === 'picking') {
                  setPickTask(t);
                } else {
                  startAction(t, 'completed', 'Mark completed');
                }
              }}
            />
          ))
        )}
      </div>
      </>
      )}

      {/* Camera scanner modal */}
      {scanTask && (
        <WarehouseTaskScanner
          open={!!scanTask}
          task={scanTask}
          authHeaders={authHeaders}
          onClose={() => setScanTask(null)}
          onCompleted={(updated) => {
            setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
          }}
        />
      )}

      {/* Floor-page assign picker (Feb 2026). Only rendered when the
          caller has `allow_floor_task_assignment` (super admin always,
          or a Floor In-charge whom super admin has flagged in
          Admin › Users). */}
      {assignTask && (
        <FloorAssignPicker
          open={!!assignTask}
          task={assignTask}
          authHeaders={authHeaders}
          onClose={() => setAssignTask(null)}
          onAssigned={(updated) => {
            setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            toast({ title: updated.assignee_email ? `Assigned to ${updated.assignee_name || updated.assignee_email}` : 'Task unassigned' });
          }}
        />
      )}

      {/* Picker qty-entry modal (Feb 2026). Handles the picking
          confirm flow with FEFO shortfall cascade. */}
      {pickTask && (
        <PickQtyModal
          open={!!pickTask}
          task={pickTask}
          authHeaders={authHeaders}
          onClose={() => { setPickTask(null); load({ silent: true }); }}
          onCompleted={(payload) => {
            if (payload?.task) {
              setTasks((prev) => prev.map((t) => (t.id === payload.task.id ? payload.task : t)));
            }
            // Cascade tasks are appended to the queue on next reload.
            if (Array.isArray(payload?.cascade_tasks) && payload.cascade_tasks.length > 0) {
              // Silent refresh so the new cards appear inline.
              load({ silent: true });
            }
          }}
        />
      )}

      {/* Confirmation dialog — deliberate two-tap (backdrop can't dismiss). */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent className="max-w-md w-[92vw] p-0 rounded-3xl overflow-hidden" data-testid="floor-confirm-dialog">
          <div className="p-6 bg-slate-950 text-white text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center mb-3">
              {confirmAction?.nextStatus === 'completed'
                ? <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                : <ArrowRight className="w-8 h-8 text-emerald-400" />}
            </div>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white font-display text-xl">
                {confirmAction?.label}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300 text-sm leading-relaxed">
                {confirmAction?.nextStatus === 'completed'
                  ? 'This will finalise the task. Do this only after the physical stock has been moved.'
                  : 'Marks the task as in progress. Colleagues will see you working on it.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          {confirmAction?.task && (
            <div className="p-5 border-b border-slate-100 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Task</div>
              <div className="font-bold text-slate-900">{confirmAction.task.product_name}</div>
              <div className="text-sm text-slate-600 mt-1">
                <span className="font-bold text-emerald-700 text-lg">{confirmAction.task.qty}</span> {confirmAction.task.uom} · {warehouseHasZones[confirmAction.task.warehouse_id] === false ? '' : `${confirmAction.task.zone || '—'} / `}{confirmAction.task.location_id || '—'} / {confirmAction.task.pallet_id || '—'}
              </div>
              {confirmAction.task.type === 'putaway'
                && confirmAction.nextStatus === 'completed'
                && warehouseHasZones[confirmAction.task.warehouse_id] !== false && (
                <label className="block mt-3" data-testid="floor-confirm-zone-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                    Zone <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
                  </span>
                  <select
                    value={confirmZone}
                    onChange={(e) => setConfirmZone(e.target.value)}
                    className="mt-1 w-full h-10 rounded-lg border-2 border-slate-200 focus:border-emerald-500 focus:outline-none px-3 text-sm bg-white"
                    data-testid="floor-confirm-zone-select"
                  >
                    <option value="">— Keep task&#39;s zone —</option>
                    {zoneOptions.map((z) => (
                      <option key={z.id} value={z.code}>
                        {z.code} — {z.name}
                      </option>
                    ))}
                  </select>
                  {zoneOptions.length === 0 && (
                    <div className="mt-1 text-[10px] text-slate-400">
                      No zones configured for this warehouse yet — set them up in Zone Management.
                    </div>
                  )}
                </label>
              )}
            </div>
          )}
          <AlertDialogFooter className="p-4 flex-col sm:flex-col gap-2">
            <AlertDialogAction
              onClick={runConfirmed}
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-base rounded-xl"
              data-testid="floor-confirm-yes"
            >
              {confirmAction?.nextStatus === 'completed' ? 'Yes, complete task' : 'Yes, start task'}
            </AlertDialogAction>
            <AlertDialogCancel
              className="w-full h-12 mt-0 border-slate-200 text-slate-600 font-semibold rounded-xl"
              data-testid="floor-confirm-cancel"
            >
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


// =====================================================================
// TaskCard — one card per task. Designed for one-handed thumb reach.
// =====================================================================
const TaskCard = ({ task, canScan, canAct, canAssign, isSuperAdmin, canDeleteLoadingPhotos, warehouseHasZones = {}, onReset, onAssign, onScan, onStart, onComplete, onLoadingUploaded }) => {
  const isCompleted = task.status === 'completed';
  const isInProgress = task.status === 'in_progress';
  const typeIcon = task.type === 'picking' ? ClipboardCheck : task.type === 'loading' ? Truck : ClipboardList;
  const TypeIcon = typeIcon;
  const accentBg = task.type === 'picking' ? 'from-amber-400 to-amber-600'
    : task.type === 'loading' ? 'from-emerald-400 to-emerald-600'
    : 'from-indigo-400 to-indigo-600';
  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${isCompleted ? 'opacity-60' : ''}`}
      data-testid={`floor-task-card-${task.id}`}
    >
      {/* Top strip — accent gradient + status + order number */}
      <div className={`h-1.5 bg-gradient-to-r ${accentBg}`} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accentBg} flex items-center justify-center flex-shrink-0`}>
            <TypeIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                task.type === 'picking' ? 'bg-amber-100 text-amber-800'
                  : task.type === 'loading' ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-indigo-100 text-indigo-800'
              }`}>
                {task.type}
              </span>
              {task.sku && (
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-bold font-mono uppercase tracking-wider bg-slate-900 text-slate-50"
                  data-testid={`floor-task-sku-${task.id}`}
                  title={`Item Code: ${task.sku}`}
                >
                  {task.sku}
                </span>
              )}
              {task.order_number && (
                <span className="text-[10px] text-slate-500 font-mono truncate">{task.order_number}</span>
              )}
              {task.type === 'loading' && task.erp_invoice_number && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 truncate max-w-[160px]"
                  data-testid={`loading-task-${task.id}-erp-invoice`}
                  title={`ERP Invoice #${task.erp_invoice_number}`}
                >
                  ERP #{task.erp_invoice_number}
                </span>
              )}
            </div>
            <div className="font-bold text-slate-900 text-base leading-tight" data-testid={`floor-task-name-${task.id}`}>
              {task.product_name || task.product_id}
            </div>
            {task.pack && (
              <div className="mt-1">
                <span
                  className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 font-mono"
                  data-testid={`floor-task-pack-${task.id}`}
                  title={`Pack: ${task.pack}`}
                >
                  {task.pack}
                </span>
              </div>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-3xl font-bold tabular-nums text-slate-900 leading-none">{task.qty}</div>
            <div className="text-[10px] uppercase text-slate-400 font-bold tracking-wider mt-0.5">{task.uom}</div>
          </div>
        </div>

        {/* Location chips — icon + label pairs. */}
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {task.warehouse_name && (
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg px-2 py-1 text-[11px] text-slate-700 font-medium">
              <Warehouse className="w-3 h-3" /> {task.warehouse_name}
            </div>
          )}
          {task.type !== 'picking' && (() => {
            // Feb 2026 — If the warehouse has no zones configured
            // (`warehouseHasZones[task.warehouse_id] === false`),
            // drop the zone prefix and show just the location chip.
            // `undefined` means we haven't probed yet — stay
            // optimistic and show zone/loc as before. Picking tasks
            // are handled by their own high-contrast LOC chip below.
            const hasZones = warehouseHasZones?.[task.warehouse_id];
            const showZone = !!task.zone && hasZones !== false;
            if (!showZone && !task.location_id) return null;
            const isStage = showZone && (task.zone || '').toUpperCase() === 'STAGE';
            return (
              <div
                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium ${
                  isStage
                    ? 'bg-amber-100 border border-amber-300 text-amber-900 font-bold'
                    : 'bg-slate-100 text-slate-700'
                }`}
                data-testid={`floor-task-zone-loc-${task.id}`}
              >
                <MapPin className="w-3 h-3" />{' '}
                {showZone ? `${task.zone}/${task.location_id || '—'}` : (task.location_id || '—')}
              </div>
            );
          })()}
          {/* Feb 2026 — Picking tasks get a dedicated, high-contrast
              LOC chip rendered BEFORE the pallet chip so the picker
              reads location and pallet without expanding anything.
              Scoped to picking so put-away / loading cards keep their
              existing combined zone-location chip. */}
          {task.type === 'picking' && (
            <div
              className="inline-flex items-center gap-1.5 bg-sky-600 text-white rounded-lg px-2.5 py-1 text-[12px] font-bold shadow-sm"
              data-testid={`floor-task-location-${task.id}`}
              title={`Location: ${task.location_id || '—'}`}
            >
              <MapPin className="w-3.5 h-3.5" />
              <span className="uppercase tracking-wider text-[9px] opacity-80">Loc</span>
              <span className="font-mono">{task.location_id || '—'}</span>
            </div>
          )}
          {task.pallet_id && (
            <div
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-mono font-semibold ${
                task.type === 'picking'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700'
              }`}
              data-testid={`floor-task-pallet-${task.id}`}
              title={`Pallet: ${task.pallet_id}`}
            >
              <Package className="w-3.5 h-3.5" />
              {task.type === 'picking' && (
                <span className="uppercase tracking-wider text-[9px] opacity-80 font-sans">Pal</span>
              )}
              {task.pallet_id}
            </div>
          )}
          {task.expiry_date && (
            <div className="inline-flex items-center gap-1 bg-red-50 border border-red-200 rounded-lg px-2 py-1 text-[11px] text-red-700 font-medium">
              Exp {formatDate(task.expiry_date)}
            </div>
          )}
          {task.trigger === 'bulk_upload' && (
            <div className="inline-flex items-center gap-1 bg-purple-50 border border-purple-200 rounded-lg px-2 py-1 text-[10px] text-purple-800 font-bold uppercase tracking-wider">
              Bulk upload
            </div>
          )}
          {task.trigger === 'return' && (
            <div className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1 text-[10px] text-orange-800 font-bold uppercase tracking-wider">
              Return
            </div>
          )}
          {task.trigger === 'manual_stage' && (
            <div className="inline-flex items-center gap-1 bg-amber-100 border border-amber-300 rounded-lg px-2 py-1 text-[10px] text-amber-900 font-bold uppercase tracking-wider">
              Stage
            </div>
          )}
        </div>

        {/* Assignee row — helps pickers see WHO is on this task at a
            glance, and gives managers/floor-incharge a clear signal
            when a task is still unassigned. When `canAssign` is true
            (super admin, or a Floor In-charge whom super admin has
            flagged via `allow_floor_task_assignment`), the strip
            doubles as a tap target that opens the assign picker. */}
        {(task.assignee_name || task.assignee_email) ? (
          <div
            className={`mt-3 flex items-center gap-2 text-[11px] text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 ${canAssign ? 'cursor-pointer hover:bg-emerald-100 active:bg-emerald-200' : ''}`}
            onClick={canAssign ? onAssign : undefined}
            data-testid={`floor-task-assignee-${task.id}`}
          >
            <UserCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-900 truncate">Assigned to {task.assignee_name || task.assignee_email}</div>
              {task.assigned_at && (
                <div className="text-[10px] text-slate-500 mt-0.5">at {formatDateTime(task.assigned_at)}</div>
              )}
            </div>
            {canAssign && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 border border-blue-200 rounded-full px-2 py-0.5 flex-shrink-0" data-testid={`floor-task-reassign-btn-${task.id}`}>
                Change
              </span>
            )}
          </div>
        ) : (
          canAssign ? (
            <button
              type="button"
              onClick={onAssign}
              className="mt-3 w-full min-h-[44px] rounded-lg border border-blue-300 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 text-blue-800 font-semibold text-xs inline-flex items-center justify-center gap-2"
              data-testid={`floor-task-assign-btn-${task.id}`}
            >
              <UserPlus className="w-4 h-4" /> Assign to staff
            </button>
          ) : (
            <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 font-semibold" data-testid={`floor-task-unassigned-${task.id}`}>
              Unassigned — waiting for a manager to hand this out
            </div>
          )
        )}

        {/* Status badge — large enough to be read at a glance from a warehouse aisle. */}
        <div className="mt-3">
          <StatusPill status={task.status} />
        </div>

        {/* Actions — 56px tall row of buttons; only visible when the user has capability. */}
        {!isCompleted && task.type === 'loading' && canAct && (
          <div className="mt-4 space-y-3">
            {/* Feb 2026 — Upload-only photo flow. Loaders can capture
                proof photos throughout the load (before/during/after)
                without triggering task completion. Photos persist via
                the new task-scoped endpoint into the same
                `orders.loading_photos` store as the terminal
                complete-loading upload, so the admin Loading Pictures
                modal + branch portal see them immediately. */}
            <FloorLoadingPhotoUpload task={task} isSuperAdmin={isSuperAdmin} canDeleteLoadingPhotos={canDeleteLoadingPhotos} />
            <LoadingUploadButton task={task} onUploaded={onLoadingUploaded} />
          </div>
        )}
        {!isCompleted && task.type !== 'loading' && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {canScan ? (
              <button
                type="button"
                onClick={onScan}
                className="h-14 rounded-2xl bg-slate-950 hover:bg-black text-white font-bold text-sm inline-flex items-center justify-center gap-2"
                data-testid={`floor-scan-btn-${task.id}`}
              >
                <ScanLine className="w-5 h-5" /> Scan
              </button>
            ) : (
              <div className="h-14 rounded-2xl bg-slate-100 text-slate-400 font-semibold text-xs inline-flex items-center justify-center px-2 text-center leading-tight">
                Scan not permitted for your role
              </div>
            )}
            {canAct && (
              isInProgress ? (
                <button
                  type="button"
                  onClick={onComplete}
                  className="h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5"
                  data-testid={`floor-complete-btn-${task.id}`}
                >
                  <CheckCircle2 className="w-5 h-5" /> Complete
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onStart}
                  className="h-14 rounded-2xl bg-white border-2 border-slate-950 text-slate-950 font-bold text-sm inline-flex items-center justify-center gap-1.5"
                  data-testid={`floor-start-btn-${task.id}`}
                >
                  Start <ArrowRight className="w-5 h-5" />
                </button>
              )
            )}
            {!canAct && (
              <div className="h-14 rounded-2xl bg-slate-50 text-slate-400 text-[11px] inline-flex items-center justify-center px-2 text-center leading-tight italic">
                Read-only role
              </div>
            )}
          </div>
        )}
        {/* Feb 2026 — Super Admin Reset. Renders on every task
            regardless of status so the operator can rewind picking,
            loading, put-away, or pallet-return tasks that got stuck
            in the wrong state. */}
        {isSuperAdmin && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onReset}
              className="w-full h-9 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-bold inline-flex items-center justify-center gap-1.5"
              data-testid={`floor-task-reset-${task.id}`}
              title="Super Admin: reset task back to Pending"
            >
              <Shield className="w-3.5 h-3.5" /> Reset Task (Super Admin)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const FloorLoadingPhotoUpload = ({ task, isSuperAdmin = false, canDeleteLoadingPhotos = false }) => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  // Feb 2026 — synchronous guard against double-clicks and slow-
  // upload retries. The `uploading` state update is async so a very
  // fast second tap (or a `change` event fired twice by iOS Safari)
  // could slip past the `disabled` prop and fire a second POST that
  // Mongo now dedups on the server, but we still want to skip the
  // pointless bytes over the wire.
  const uploadingRef = React.useRef(false);
  // Two separate refs — the camera picker carries `capture=environment`
  // so the OS opens the rear camera app; the gallery picker omits it
  // so the OS opens the photo library. On desktop both fall back to
  // the same file chooser, which is fine.
  const cameraRef = React.useRef(null);
  const galleryRef = React.useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(
        `${API}/admin/warehouse/tasks/${task.id}/loading-photos`,
        { headers: authHeaders },
      );
      setPhotos(r.data?.photos || []);
    } catch (_e) {
      // Silent — the upload button still works even if the read
      // failed. `photos.length === 0` will simply skip the strip.
    } finally {
      setPhotosLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const upload = async (fileList, sourceRef) => {
    const files = Array.from(fileList || []).filter(
      (f) => f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name),
    );
    if (!files.length) return;
    // Hard synchronous guard — bail immediately if a previous
    // upload is still in flight so a double-click or slow network
    // can't fire two POSTs.
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const r = await axios.post(
        `${API}/admin/warehouse/tasks/${task.id}/upload-loading-photos`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      const added = r.data?.count || 0;
      const skipped = r.data?.skipped_duplicates || 0;
      toast({
        title: added > 0 ? 'Photos uploaded' : 'No new photos',
        description: skipped > 0
          ? `${added} added · ${skipped} skipped (already uploaded).`
          : `${added} photo${added === 1 ? '' : 's'} added to this order.`,
      });
      await load();
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.detail || err?.message || 'Try again.',
      });
    } finally {
      uploadingRef.current = false;
      setUploading(false);
      // Reset both inputs so re-uploading the same file works from
      // either source.
      if (sourceRef?.current) sourceRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  return (
    <div data-testid={`floor-loading-photos-${task.id}`}>
      {/* Feb 2026 — split the single "Upload" button into two
          side-by-side actions so the loader can pick between taking a
          fresh photo with the camera OR choosing an existing shot
          from the device gallery. Both funnel into the same upload
          handler + endpoint. On desktop the two triggers collapse to
          the same file chooser, which is fine. */}
      <div className="grid grid-cols-2 gap-2">
        <label
          htmlFor={`floor-loading-photos-camera-${task.id}`}
          className={`h-14 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer ${uploading ? 'opacity-70 pointer-events-none' : ''}`}
          data-testid={`floor-loading-photos-take-${task.id}`}
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          {uploading ? 'Uploading…' : 'Take Photo'}
          <input
            ref={cameraRef}
            id={`floor-loading-photos-camera-${task.id}`}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(e) => upload(e.target.files, cameraRef)}
            className="hidden"
            data-testid={`floor-loading-photos-camera-input-${task.id}`}
          />
        </label>
        <label
          htmlFor={`floor-loading-photos-gallery-${task.id}`}
          className={`h-14 rounded-2xl bg-slate-700 hover:bg-slate-800 text-white font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer ${uploading ? 'opacity-70 pointer-events-none' : ''}`}
          data-testid={`floor-loading-photos-choose-${task.id}`}
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Images className="w-5 h-5" />}
          {uploading ? 'Uploading…' : 'Choose from Gallery'}
          <input
            ref={galleryRef}
            id={`floor-loading-photos-gallery-${task.id}`}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => upload(e.target.files, galleryRef)}
            className="hidden"
            data-testid={`floor-loading-photos-gallery-input-${task.id}`}
          />
        </label>
      </div>
      {/* Feb 2026 — Loading Pictures section (attribution grid).
          Renders every photo already captured for THIS order — loader
          self-uploads + admin on-behalf uploads together — with each
          card labelled by uploader name, credited loader, and upload
          timestamp. Shares the same component the AdminOrders detail
          view uses so both audiences see identical attribution. */}
      {!photosLoading && (
        <LoadingPicturesSection
          orderId={task.order_id}
          photos={photos}
          API={API}
          authHeaders={authHeaders}
          testidPrefix={`floor-loading-photos-${task.id}`}
          isSuperAdmin={isSuperAdmin}
          canDelete={canDeleteLoadingPhotos}
        />
      )}
    </div>
  );
};

const LoadingUploadButton = ({ task, onUploaded }) => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  // Feb 2026 — synchronous double-click guard: `disabled` on the
  // button is React-async, so a very fast second tap could slip
  // through and fire a second POST. This ref short-circuits at the
  // top of upload().
  const uploadingRef = React.useRef(false);
  // Feb 2026 — Pallet Ledger Phase 2 → dual-type. Loaders now record
  // pallets for BOTH plastic AND wooden per order (mixed shipments).
  // We keep two independent qty states + saved snapshots per type.
  const initialByType = task?.order?.pallets_sent_by_type || {};
  const [palletsPlastic, setPalletsPlastic] = useState(
    initialByType.plastic != null ? String(initialByType.plastic) : ''
  );
  const [palletsWooden, setPalletsWooden] = useState(
    initialByType.wooden != null ? String(initialByType.wooden) : ''
  );
  const [savingPalletsType, setSavingPalletsType] = useState('');
  const [palletsSavedFor, setPalletsSavedFor] = useState({
    plastic: initialByType.plastic != null ? String(initialByType.plastic) : '',
    wooden: initialByType.wooden != null ? String(initialByType.wooden) : '',
  });

  // Feb 2026 — Hydrate the loader's inputs on mount from the backend
  // summary endpoint so prior saves survive a page refresh.
  React.useEffect(() => {
    const orderId = task?.order?.id || task?.order_id;
    if (!orderId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/orders/${orderId}/pallets`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        const t = data?.pallets_sent_by_type || {};
        if (t.plastic != null) {
          setPalletsPlastic(String(t.plastic));
          setPalletsSavedFor((prev) => ({ ...prev, plastic: String(t.plastic) }));
        }
        if (t.wooden != null) {
          setPalletsWooden(String(t.wooden));
          setPalletsSavedFor((prev) => ({ ...prev, wooden: String(t.wooden) }));
        }
      } catch { /* silent — first time will start empty */ }
    })();
    return () => { cancelled = true; };
  }, [task?.order?.id, task?.order_id, authHeaders]);

  const savePalletsSent = async (palletType) => {
    const orderId = task?.order?.id || task?.order_id;
    if (!orderId) return;
    const raw = palletType === 'plastic' ? palletsPlastic : palletsWooden;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) {
      toast({ title: 'Enter a non-negative pallet count' });
      return;
    }
    setSavingPalletsType(palletType);
    try {
      await axios.post(
        `${API}/admin/orders/${orderId}/pallets-sent?pallet_type=${palletType}`,
        { pallets_sent: n, notes: '' },
        { headers: authHeaders },
      );
      toast({ title: `${palletType.charAt(0).toUpperCase() + palletType.slice(1)} pallets recorded (${n})` });
      setPalletsSavedFor((prev) => ({ ...prev, [palletType]: String(n) }));
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e?.message });
    } finally {
      setSavingPalletsType('');
    }
  };

  const onPick = (e) => {
    setErr('');
    const chosen = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name));
    // Append instead of replacing so the operator can capture the
    // goods photo, then the seal photo, without losing the first one.
    // Dedupe by (name, size, lastModified) tuple to catch the "same
    // file picked twice" case.
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}::${f.size}::${f.lastModified}`));
      const merged = [...prev];
      for (const f of chosen) {
        const key = `${f.name}::${f.size}::${f.lastModified}`;
        if (!seen.has(key)) { merged.push(f); seen.add(key); }
      }
      return merged;
    });
    e.target.value = '';
  };

  const upload = async () => {
    if (!files.length) { setErr('Add at least one photo.'); return; }
    if (uploadingRef.current) return;   // guard against fast double-taps
    uploadingRef.current = true;
    setUploading(true);
    setErr('');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(
        `${API}/admin/warehouse/tasks/${task.id}/complete-loading`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      onUploaded?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  // Feb 2026 — Loading gate: the "Upload & complete loading" button
  // (which is the last step before ops flips the order to
  // `picked_up`) MUST NOT fire until the loader has entered AND
  // saved pallet quantities for at least one pallet type. Compare
  // the last-saved snapshot against the current input to catch the
  // case where the loader typed a new number but forgot to hit
  // Save — we treat unsaved edits as "not saved yet".
  const plasticSaved = palletsSavedFor.plastic !== '' && palletsSavedFor.plastic === palletsPlastic;
  const woodenSaved = palletsSavedFor.wooden !== '' && palletsSavedFor.wooden === palletsWooden;
  const palletsCommitted = plasticSaved || woodenSaved;

  return (
    <div data-testid={`loading-upload-${task.id}`}>
      {/* Feb 2026 — split the single "Add photos" button into
          Take Photo + Choose from Gallery so the loader isn't forced
          into the camera app when the shot they need is already on
          the device. Both stream into the same `files` state so the
          rest of the flow (list, remove, upload) is unchanged. */}
      <div className="grid grid-cols-2 gap-2">
        <label
          htmlFor={`loading-file-camera-${task.id}`}
          className="h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer"
          data-testid={`loading-file-take-${task.id}`}
        >
          <Camera className="w-5 h-5" />
          Take Photo
          <input
            id={`loading-file-camera-${task.id}`}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={onPick}
            className="hidden"
            data-testid={`loading-file-camera-input-${task.id}`}
          />
        </label>
        <label
          htmlFor={`loading-file-gallery-${task.id}`}
          className="h-14 rounded-2xl bg-slate-700 hover:bg-slate-800 text-white font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer"
          data-testid={`loading-file-choose-${task.id}`}
        >
          <Images className="w-5 h-5" />
          Choose from Gallery
          <input
            id={`loading-file-gallery-${task.id}`}
            type="file"
            accept="image/*"
            multiple
            onChange={onPick}
            className="hidden"
            data-testid={`loading-file-gallery-input-${task.id}`}
          />
        </label>
      </div>
      {files.length > 0 && (
        <div
          className="mt-2 text-xs font-semibold text-emerald-800"
          data-testid={`loading-file-selected-count-${task.id}`}
        >
          {files.length} photo{files.length === 1 ? '' : 's'} selected — tap another action above to add more
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
              <Camera className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
              <span className="flex-1 truncate font-medium text-emerald-900">{f.name}</span>
              <span className="text-emerald-700 text-[10px]">{Math.round(f.size / 1024)} KB</span>
              <button type="button" onClick={() => removeFile(i)} className="text-emerald-800 hover:text-red-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {err && <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
      {/* Feb 2026 — Pallet Ledger Phase 2. The loader records how many
          pallets are physically going on the truck for this order.
          Saves independently of the photo upload so we capture the
          count even if the loader hasn't taken photos yet. */}
      <div
        className="mt-3 rounded-2xl bg-indigo-50/60 border border-indigo-200 p-3"
        data-testid={`loading-pallets-sent-block-${task.id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wide text-indigo-900">Pallets sent with this order</div>
        </div>

        {/* Plastic row — v9: `min-w-0` on the input lets `flex-1` shrink
            below content-min on narrow phones, and `shrink-0` on the
            button prevents it from being clipped by the parent card. */}
        <div className="flex items-center gap-2 mb-2" data-testid={`loading-pallets-plastic-row-${task.id}`}>
          <div className="w-16 shrink-0 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-indigo-800">
            Plastic
            {palletsSavedFor.plastic !== '' && palletsSavedFor.plastic === palletsPlastic && (
              <span className="text-[9px] font-bold text-emerald-700" data-testid={`loading-pallets-plastic-saved-${task.id}`}>✓</span>
            )}
          </div>
          <input
            type="number"
            min="0"
            value={palletsPlastic}
            onChange={(e) => setPalletsPlastic(e.target.value)}
            placeholder="e.g. 4"
            className="flex-1 min-w-0 h-11 rounded-xl border border-indigo-200 bg-white px-3 text-sm"
            data-testid={`loading-pallets-plastic-input-${task.id}`}
          />
          <button
            type="button"
            onClick={() => savePalletsSent('plastic')}
            disabled={savingPalletsType === 'plastic' || palletsPlastic === '' || palletsPlastic === palletsSavedFor.plastic}
            className="shrink-0 h-11 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`loading-pallets-plastic-save-${task.id}`}
          >
            {savingPalletsType === 'plastic' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>

        {/* Wooden row */}
        <div className="flex items-center gap-2" data-testid={`loading-pallets-wooden-row-${task.id}`}>
          <div className="w-16 shrink-0 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-amber-800">
            Wooden
            {palletsSavedFor.wooden !== '' && palletsSavedFor.wooden === palletsWooden && (
              <span className="text-[9px] font-bold text-emerald-700" data-testid={`loading-pallets-wooden-saved-${task.id}`}>✓</span>
            )}
          </div>
          <input
            type="number"
            min="0"
            value={palletsWooden}
            onChange={(e) => setPalletsWooden(e.target.value)}
            placeholder="e.g. 2"
            className="flex-1 min-w-0 h-11 rounded-xl border border-amber-200 bg-white px-3 text-sm"
            data-testid={`loading-pallets-wooden-input-${task.id}`}
          />
          <button
            type="button"
            onClick={() => savePalletsSent('wooden')}
            disabled={savingPalletsType === 'wooden' || palletsWooden === '' || palletsWooden === palletsSavedFor.wooden}
            className="shrink-0 h-11 px-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`loading-pallets-wooden-save-${task.id}`}
          >
            {savingPalletsType === 'wooden' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>

        <div className="text-[11px] text-indigo-800 mt-2">
          Debits the branch&apos;s pallet balance at the latest GRN unit price. Branches reconcile this on their receive screen.
        </div>
      </div>
      {files.length > 0 && (
        <>
          {/* Feb 2026 — Gate warning: block finalising loading (and
              thus the order's picked_up transition) until pallet
              quantities have been saved. Shown ABOVE the Upload
              button so the loader sees exactly why the action is
              disabled. */}
          {!palletsCommitted && (
            <div
              className="mt-3 text-xs bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-amber-900 font-semibold"
              data-testid={`loading-pallets-required-warning-${task.id}`}
            >
              Please enter and save pallet quantities before updating the order status.
            </div>
          )}
          <button
            type="button"
            onClick={upload}
            disabled={uploading || !palletsCommitted}
            className="mt-3 h-12 w-full rounded-2xl bg-slate-950 hover:bg-black text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`loading-complete-btn-${task.id}`}
            title={!palletsCommitted ? 'Save pallet quantities first' : undefined}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            {uploading ? 'Uploading…' : 'Upload & complete loading'}
          </button>
        </>
      )}
    </div>
  );
};


const StatusPill = ({ status }) => {
  const map = {
    pending:     { label: 'Pending',      classes: 'bg-slate-200 text-slate-800' },
    in_progress: { label: 'In progress',  classes: 'bg-blue-500 text-white' },
    completed:   { label: 'Completed',    classes: 'bg-emerald-500 text-white' },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${s.classes}`}>
      {s.label}
    </span>
  );
};


// =====================================================================
// PalletReturnsFloorTab — floor-side queue of pending physical returns
// awaiting dock confirmation. Each row shows order ref + branch name +
// expected qty; the floor user enters what actually arrived and taps
// Receive. Backend credits warehouse inventory by the ACTUAL qty and
// flags a discrepancy when it differs from the branch's declared count.
// =====================================================================
// =====================================================================
// Feb 2026 v3 — Floor-Mode Receiving tab
// =====================================================================
//
// Reads an ASN by number (scan or type), shows the expected line items
// with per-line editable qty / production date / expiry date, and
// commits via `POST /admin/asn/receive-scan`. The backend patches the
// ASN's line items with the actuals, logs any variance, and runs the
// standard receive pipeline (Stage placement + put-away task per line).
const ReceivingFloorTab = ({ authHeaders, toast }) => {
  const [scan, setScan] = useState('');
  const [asn, setAsn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [entry, setEntry] = useState({
    item_number: '',
    qty: '',
    pallet_no: '',
    expiry_date: '',
    production_date: '',
  });
  const [saving, setSaving] = useState(false);
  // Feb 2026 — camera-scanner support for the Receiving flow.
  // `scanTarget` is `''` (closed), `'asn'` (ASN barcode), `'item'`
  // (SKU barcode) or `'pallet'` (pallet barcode). The generic
  // BarcodeScannerModal decodes Code-128/39/EAN/QR and pipes the
  // decoded text back into the correct input.
  const [scanTarget, setScanTarget] = useState('');
  // Feb 2026 v7 — Pre-assigned ASN queue. `myAsns` holds every open
  // ASN whose `receiver_email` matches the signed-in user (super admin
  // sees every open ASN). Powers the "My assigned ASNs" tap-to-load
  // list so the receiver doesn't have to scan the ASN number by hand.
  const [myAsns, setMyAsns] = useState([]);
  const [myLoading, setMyLoading] = useState(true);

  const loadMyAsns = React.useCallback(async () => {
    setMyLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/asn/my-receiving`, { headers: authHeaders });
      setMyAsns(Array.isArray(data) ? data : []);
    } catch (err) {
      // Endpoint 403 for users without asn.view permission — surface a
      // gentle empty state, not an error toast (they can still scan).
      setMyAsns([]);
    } finally {
      setMyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  useEffect(() => { loadMyAsns(); }, [loadMyAsns]);

  const lookup = async (eOrValue) => {
    // Support two invocation styles:
    //   * Form submit → receives a SyntheticEvent (call preventDefault).
    //   * Camera-scan callback → receives a raw string to look up.
    let q;
    if (typeof eOrValue === 'string') {
      q = eOrValue.trim();
    } else {
      if (eOrValue && eOrValue.preventDefault) eOrValue.preventDefault();
      q = scan.trim();
    }
    if (!q) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/asn-by-number/${encodeURIComponent(q)}`,
        { headers: authHeaders },
      );
      setAsn(data);
    } catch (err) {
      toast({ title: 'ASN not found', description: err.response?.data?.detail || `No ASN "${q}"` });
      setAsn(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAsn = async () => {
    if (!asn) return;
    try {
      const { data } = await axios.get(
        `${API}/admin/asn-by-number/${encodeURIComponent(asn.asn_number)}`,
        { headers: authHeaders },
      );
      setAsn(data);
    } catch {}
  };

  const submitScan = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!asn) return;
    if (!entry.item_number.trim() || Number(entry.qty || 0) <= 0
        || !entry.pallet_no.trim() || !entry.expiry_date) {
      toast({ title: 'Fill every field', description: 'Item Number, Qty, Pallet # and Expiry Date are required.' });
      return;
    }
    // 2026-10 bugfix — warn BEFORE submitting when the scanned item
    // isn't on the currently open ASN's manifest. Goods for one ASN
    // were getting scanned into a different, wrong-but-open ASN
    // (receiver had the wrong card open on their device); the
    // backend still records these as `is_extra` for audit, but the
    // operator never saw a warning until after the scan was already
    // committed. Catch it here, before the write.
    const scannedItem = entry.item_number.trim().toUpperCase();
    const onManifest = (asn.line_items || []).some((ln) => (
      String(ln.sku || '').toUpperCase() === scannedItem
      || String(ln.product_id || '') === entry.item_number.trim()
    ));
    if (!onManifest) {
      const proceed = window.confirm(
        `"${entry.item_number.trim()}" is not on ${asn.asn_number}'s manifest.\n\n` +
        `Double-check you have the right ASN open — goods for a different ASN scanned here will be recorded as an untracked extra on ${asn.asn_number} instead of their own ASN.\n\n` +
        `Continue scanning it into ${asn.asn_number} anyway?`,
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${asn.id}/scan-item`,
        {
          item_number: entry.item_number.trim(),
          qty: Number(entry.qty),
          pallet_no: entry.pallet_no.trim(),
          expiry_date: entry.expiry_date,
          production_date: entry.production_date || '',
        },
        { headers: authHeaders },
      );
      toast({
        title: data.scan?.is_extra ? 'Extra item scanned' : 'Scan recorded',
        description: `${data.scan?.pallet_no} · ${data.scan?.qty} ${data.scan?.uom}`,
      });
      // Reset for the next scan — a single SKU can span multiple
      // pallets each with their own expiry, so keep item_number and
      // production_date but clear qty / pallet_no / expiry_date so
      // the operator explicitly re-scans each pallet.
      setEntry((e0) => ({
        item_number: e0.item_number,
        qty: '',
        pallet_no: '',
        expiry_date: '',
        production_date: e0.production_date,
      }));
      refreshAsn();
    } catch (err) {
      toast({ title: 'Scan failed', description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const undoScan = async (scanId) => {
    if (!asn) return;
    if (!window.confirm('Undo this scan? The placement + put-away task will be deleted.')) return;
    try {
      await axios.delete(`${API}/admin/asn/${asn.id}/scan-item/${scanId}`, { headers: authHeaders });
      toast({ title: 'Scan undone' });
      refreshAsn();
    } catch (err) {
      toast({ title: 'Undo failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  const completeReceipt = async (opts = {}) => {
    if (!asn) return;
    const finalizeAsPartial = !!opts.finalizeAsPartial;
    if (!finalizeAsPartial) {
      if (!window.confirm(`Complete receipt for ${asn.asn_number}? The ASN will flip to Received and any qty variance will be logged.`)) return;
    }
    setCompleting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${asn.id}/complete-receipt`,
        finalizeAsPartial
          ? { finalize_as_partial: true, partial_note: opts.partialNote || '' }
          : {},
        { headers: authHeaders },
      );
      const skipMsg = data.stock_skipped_duplicate_count
        ? ` · skipped ${data.stock_skipped_duplicate_count} duplicate scan(s)`
        : '';
      toast({
        title: data.finalized_as_partial ? 'ASN received (partial)' : 'ASN received',
        description: (data.variance || []).length
          ? `${data.scans_count} scans · ${data.variance.length} line(s) with variance${skipMsg}`
          : `${data.scans_count} scans committed${skipMsg}`,
      });
      setAsn(null);
      setScan('');
      setEntry({ item_number: '', qty: '', pallet_no: '', expiry_date: '', production_date: '' });
      // Refresh My ASNs list — the completed ASN drops off since it's
      // no longer in status expected|draft.
      loadMyAsns();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 2026-02 — Full-scan gate: backend returns 409 with
      // `{code:"incomplete_scan", shortfall_lines, message}` when
      // the operator tried to complete an under-scanned ASN. Prompt
      // once with the shortfall summary; if confirmed, re-call with
      // `finalize_as_partial=true`.
      if (err.response?.status === 409 && detail?.code === 'incomplete_scan') {
        const lines = (detail.shortfall_lines || []).map((l) =>
          `  • ${l.sku || l.product_id}: expected ${l.expected_qty}, scanned ${l.actual_qty} (short ${Math.abs(l.diff)})`,
        ).join('\n');
        const ok = window.confirm(
          `${detail.shortfall_lines?.length || 0} line(s) still short:\n\n${lines}\n\n` +
          `Close this ASN as a PARTIAL receipt and keep it open for the missing qty via "Reopen"?`,
        );
        if (ok) {
          const note = window.prompt('Optional note for audit (why partial?):', '') || '';
          setCompleting(false);
          return completeReceipt({ finalizeAsPartial: true, partialNote: note });
        }
      } else {
        toast({ title: 'Complete failed', description: typeof detail === 'string' ? detail : detail?.message || 'Try again.' });
      }
    } finally {
      setCompleting(false);
    }
  };

  const scans = (asn && (asn.receipt_scans || [])) || [];
  const expectedTotal = (asn?.line_items || []).reduce((s, l) => s + Number(l.qty || 0), 0);
  const scannedTotal = scans.reduce((s, r) => s + Number(r.qty || 0), 0);

  /**
   * Feb 2026 — Handheld-scanner tally.
   * Builds one row per SKU showing expected / scanned / balance. Extra
   * SKUs that appear in `receipt_scans` but not in `line_items` land
   * at the bottom flagged as "extra" so the operator still sees them
   * in the tally. Keyed on `item_number` (normalised) so scans made
   * before the product record was linked still tally correctly.
   */
  const norm = (v) => String(v || '').trim().toUpperCase();
  const tally = useMemo(() => {
    if (!asn) return [];
    const rows = new Map();
    for (const l of (asn.line_items || [])) {
      // ASN line schema is `{sku, name, qty, uom}` (see backend
      // `_clean_line_items`). `receipt_scans` writes `item_number`
      // (already upper-cased) so we normalise both sides to a common
      // key so the match survives casing / whitespace differences.
      const key = norm(l.sku);
      if (!key) continue;
      rows.set(key, {
        item_number: l.sku,
        product_name: l.name || '',
        uom: l.uom || 'ctn',
        expected: Number(l.qty || 0),
        scanned: 0,
        pallets: 0,
        is_extra: false,
        line_no: (l.line_no != null && l.line_no !== '') ? l.line_no : null,
      });
    }
    for (const s of scans) {
      const key = norm(s.item_number);
      if (!key) continue;
      const q = Number(s.qty || 0);
      if (rows.has(key)) {
        const r = rows.get(key);
        r.scanned += q;
        r.pallets += 1;
      } else {
        rows.set(key, {
          item_number: s.item_number,
          product_name: s.product_name || '',
          uom: s.uom || 'ctn',
          expected: 0,
          scanned: q,
          pallets: 1,
          is_extra: true,
          line_no: null,
        });
      }
    }
    return [...rows.values()].map((r) => ({
      ...r,
      balance: Math.max(0, r.expected - r.scanned),
      over: r.scanned > r.expected && r.expected > 0,
    }));
  }, [asn, scans]);

  // Currently-typed / scanned SKU — used to surface the smart-highlight
  // "Expected vs. Balance" card right above the qty input.
  const matchedTally = useMemo(() => {
    const key = norm(entry.item_number);
    if (!key) return null;
    return tally.find((r) => norm(r.item_number) === key) || null;
  }, [entry.item_number, tally]);

  return (
    <div className="p-4 space-y-4" data-testid="floor-receiving-tab">
      {/* Feb 2026 v7 — Pre-assigned ASN queue. Every ASN whose receiver
          is the signed-in user (super admin sees ALL open ASNs) lands
          here as a tap-to-load chip. The receiver no longer has to
          scan the ASN number by hand — one tap → auto-lookup. */}
      {!asn && (myAsns.length > 0 || myLoading) && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3" data-testid="floor-receiving-my-asns">
          <div className="flex items-center gap-2 mb-2">
            <PackagePlus className="w-4 h-4 text-emerald-700" />
            <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-900">
              My assigned ASNs {myLoading ? '' : `(${myAsns.length})`}
            </div>
            <button
              type="button"
              onClick={loadMyAsns}
              className="ml-auto text-[10px] font-bold text-emerald-800 hover:text-emerald-900 uppercase tracking-wider inline-flex items-center gap-1"
              data-testid="floor-receiving-my-asns-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          {myLoading ? (
            <div className="text-xs text-emerald-800/70 py-1">Loading…</div>
          ) : myAsns.length === 0 ? (
            <div className="text-xs text-emerald-800/70 py-1">
              No ASNs assigned to you yet — an admin will assign one from the Goods Receiving page.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {myAsns.map((a) => {
                const expected = (a.line_items || []).reduce((s, l) => s + Number(l.qty || 0), 0);
                const scanned = (a.receipt_scans || []).reduce((s, r) => s + Number(r.qty || 0), 0);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      setScan(a.asn_number);
                      lookup(a.asn_number);
                    }}
                    className="flex flex-col items-start gap-0.5 rounded-xl bg-white hover:bg-emerald-100 border-2 border-emerald-300 hover:border-emerald-500 px-3 py-2 text-left transition min-w-[180px]"
                    data-testid={`floor-receiving-my-asn-${a.asn_number}`}
                  >
                    <span className="font-mono font-bold text-[13px] text-emerald-900">{a.asn_number}</span>
                    <span className="text-[10px] text-emerald-800/80">
                      {a.customer_name || a.vehicle_number || '—'}
                    </span>
                    <span className="text-[10px] tabular-nums text-emerald-800/70">
                      Scanned <b>{scanned}</b> / expected {expected}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <form onSubmit={lookup} className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[220px] block">
          <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Scan / enter ASN number</span>
          <div className="relative mt-1">
            <ScanLine className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              autoFocus
              type="text"
              value={scan}
              onChange={(e) => setScan(String(e.target.value || '').toUpperCase())}
              placeholder="e.g. ASN-000001"
              autoCapitalize="characters"
              autoCorrect="off"
              className="w-full h-12 rounded-2xl bg-white border-2 border-slate-200 focus:border-emerald-400 focus:outline-none pl-9 pr-12 text-sm font-mono uppercase"
              style={{ textTransform: 'uppercase' }}
              data-testid="floor-receiving-scan-input"
              autoComplete="off"
              spellCheck="false"
            />
            <button
              type="button"
              onClick={() => setScanTarget('asn')}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
              title="Scan ASN barcode with camera"
              aria-label="Scan ASN barcode with camera"
              data-testid="floor-receiving-camera-asn"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>
        </label>
        <button
          type="submit"
          disabled={loading || !scan.trim()}
          className="h-12 px-5 rounded-2xl bg-slate-950 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="floor-receiving-scan-submit"
        >
          {loading ? 'Looking up…' : 'Load ASN'}
        </button>
      </form>

      {asn && (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="floor-receiving-asn-card">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-bold text-[#0078D4]" data-testid="floor-receiving-asn-number">{asn.asn_number}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                asn.status === 'expected' ? 'bg-amber-100 text-amber-800' :
                asn.status === 'received' ? 'bg-emerald-100 text-emerald-700' :
                'bg-slate-200 text-slate-700'
              }`}>{asn.status}</span>
              <span className="text-[10px] font-bold tabular-nums text-slate-600 ml-auto">
                Scanned <b>{scannedTotal}</b> / expected {expectedTotal}
              </span>
            </div>
            <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-3">
              {asn.customer_name && <span>{asn.customer_name}</span>}
              {asn.vehicle_number && <span>· Vehicle {asn.vehicle_number}</span>}
            </div>
          </div>

          {asn.status !== 'expected' && asn.status !== 'draft' ? (
            <div className="p-6 text-center text-sm text-slate-600" data-testid="floor-receiving-locked">
              This ASN is <b>{asn.status}</b>. Ask an admin to reopen before scanning.
            </div>
          ) : (
            <>
              {/* Scan-per-item form */}
              <form onSubmit={submitScan} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 border-b border-slate-100">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Item Number</span>
                  <div className="relative mt-0.5">
                    <input
                      type="text" value={entry.item_number}
                      onChange={(e) => setEntry({ ...entry, item_number: e.target.value })}
                      placeholder="Scan or type SKU"
                      className="w-full h-11 rounded-lg border-2 border-slate-200 focus:border-emerald-400 focus:outline-none pl-3 pr-12 text-sm font-mono"
                      data-testid="floor-recv-item-input" autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setScanTarget('item')}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
                      title="Scan SKU barcode with camera"
                      aria-label="Scan SKU barcode with camera"
                      data-testid="floor-recv-item-camera"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Qty</span>
                  {/* Feb 2026 — Smart highlight. When the operator has
                      typed / scanned a SKU that matches an ASN line
                      (or a previously-scanned extra), show the live
                      Expected vs. Balance ribbon right above the qty
                      input so they know how much is left before they
                      type. Green = fully received; amber = still
                      remaining; rose = OVER expected; slate = extra
                      SKU (not on this ASN). */}
                  {matchedTally && (() => {
                    const t = matchedTally;
                    const isDone = !t.is_extra && t.expected > 0 && t.balance === 0 && !t.over;
                    const isOver = t.over;
                    const isExtra = t.is_extra;
                    const ring = isOver ? 'border-rose-300 bg-rose-50'
                                : isDone ? 'border-emerald-300 bg-emerald-50'
                                : isExtra ? 'border-slate-300 bg-slate-50'
                                : 'border-amber-300 bg-amber-50';
                    const label = isOver ? 'OVER-SCANNED'
                                : isDone ? 'COMPLETE ✓'
                                : isExtra ? 'EXTRA (not on ASN)'
                                : 'REMAINING';
                    const labelColor = isOver ? 'text-rose-700'
                                : isDone ? 'text-emerald-700'
                                : isExtra ? 'text-slate-700'
                                : 'text-amber-700';
                    return (
                      <div
                        className={`mt-0.5 mb-1 rounded-lg border-2 ${ring} p-2 flex items-center gap-3`}
                        data-testid="floor-recv-smart-highlight"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 truncate">
                            {t.item_number} · {t.product_name || 'unknown'}
                          </div>
                          <div className="mt-0.5 flex items-baseline gap-2 flex-wrap text-[11px] tabular-nums">
                            <span className="text-slate-500">Expected</span>
                            <span className="font-bold text-slate-900" data-testid="floor-recv-highlight-expected">{t.expected}</span>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-500">Scanned</span>
                            <span className="font-bold text-slate-900" data-testid="floor-recv-highlight-scanned">{t.scanned}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-[9px] uppercase tracking-wider font-bold ${labelColor}`}>{label}</div>
                          <div
                            className={`text-2xl leading-none font-black tabular-nums ${
                              isOver ? 'text-rose-700'
                              : isDone ? 'text-emerald-700'
                              : isExtra ? 'text-slate-700'
                              : 'text-amber-700'
                            }`}
                            data-testid="floor-recv-highlight-balance"
                          >
                            {isOver ? `+${t.scanned - t.expected}` : (isExtra ? t.scanned : t.balance)}
                          </div>
                          <div className="text-[9px] text-slate-500">{t.uom}</div>
                        </div>
                      </div>
                    );
                  })()}
                  <input
                    type="number" min="1" value={entry.qty}
                    onChange={(e) => setEntry({ ...entry, qty: e.target.value })}
                    className="mt-0.5 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-emerald-400 focus:outline-none px-3 text-sm tabular-nums font-bold"
                    data-testid="floor-recv-qty-input"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pallet #</span>
                  <div className="relative mt-0.5">
                    <input
                      type="text" value={entry.pallet_no}
                      onChange={(e) => setEntry({ ...entry, pallet_no: String(e.target.value || '').toUpperCase() })}
                      placeholder="Scan pallet barcode"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck="false"
                      className="w-full h-11 rounded-lg border-2 border-slate-200 focus:border-emerald-400 focus:outline-none pl-3 pr-12 text-sm font-mono uppercase"
                      style={{ textTransform: 'uppercase' }}
                      data-testid="floor-recv-pallet-input" autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setScanTarget('pallet')}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
                      title="Scan pallet barcode with camera"
                      aria-label="Scan pallet barcode with camera"
                      data-testid="floor-recv-pallet-camera"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Expiry Date</span>
                  <input
                    type="date" value={entry.expiry_date}
                    onChange={(e) => setEntry({ ...entry, expiry_date: e.target.value })}
                    className="mt-0.5 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-emerald-400 focus:outline-none px-3 text-sm"
                    data-testid="floor-recv-exp-input"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Production Date (optional but recommended for FEFO)</span>
                  <input
                    type="date" value={entry.production_date}
                    onChange={(e) => setEntry({ ...entry, production_date: e.target.value })}
                    className="mt-0.5 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-emerald-400 focus:outline-none px-3 text-sm"
                    data-testid="floor-recv-prod-input"
                  />
                </label>
                <button
                  type="submit" disabled={saving}
                  className="sm:col-span-2 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold text-sm inline-flex items-center justify-center gap-2"
                  data-testid="floor-recv-submit"
                >
                  <ScanLine className="w-4 h-4" /> {saving ? 'Adding…' : 'Add Scan'}
                </button>
              </form>

              {/* Scans list — grouped by SKU so the operator instantly
                  sees when one line spans multiple pallets each with
                  their own expiry date. Now paints its group card
                  green once the SKU's balance hits zero (Feb 2026). */}
              {scans.length > 0 && (() => {
                const groups = new Map();
                scans.forEach((s) => {
                  const key = s.product_id || s.item_number || '__unknown__';
                  if (!groups.has(key)) {
                    groups.set(key, {
                      item_number: s.item_number,
                      product_name: s.product_name,
                      uom: s.uom || 'ctn',
                      is_extra: !!s.is_extra,
                      rows: [],
                    });
                  }
                  const g = groups.get(key);
                  g.rows.push(s);
                  if (s.is_extra) g.is_extra = true;
                });
                const tallyByKey = new Map(tally.map((t) => [norm(t.item_number), t]));
                return (
                  <div className="divide-y divide-slate-100" data-testid="floor-receiving-scans-list">
                    {[...groups.values()].map((g) => {
                      const gQty = g.rows.reduce((a, r) => a + Number(r.qty || 0), 0);
                      const tRow = tallyByKey.get(norm(g.item_number));
                      const isDone = tRow && !tRow.is_extra && tRow.expected > 0 && tRow.balance === 0 && !tRow.over;
                      return (
                        <div
                          key={g.item_number}
                          className={`p-3 ${isDone ? 'bg-emerald-50 border-l-4 border-emerald-400' : ''}`}
                          data-testid={`floor-recv-scan-group-${g.item_number}`}
                          data-complete={isDone ? 'true' : 'false'}
                        >
                          <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                            <span className="font-mono font-bold text-[#0078D4]">{g.item_number}</span>
                            <span className="text-slate-950 truncate">{g.product_name || '(unknown product)'}</span>
                            {g.is_extra && (
                              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">extra</span>
                            )}
                            {isDone && (
                              <span
                                className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white"
                                data-testid={`floor-recv-scan-group-done-${g.item_number}`}
                              >
                                complete
                              </span>
                            )}
                            <span className="ml-auto text-[10px] font-bold tabular-nums text-slate-700">
                              {g.rows.length} pallet{g.rows.length === 1 ? '' : 's'} · <b>{gQty}</b> {g.uom}
                              {/* Feb 2026 — inline balance so handheld
                                  operators see it on the same summary
                                  line without scrolling to the tally.
                                  Amber = remaining, emerald = done,
                                  rose = over-received. Suppressed for
                                  extras / non-ASN SKUs (no expected
                                  qty to balance against). */}
                              {tRow && !tRow.is_extra && tRow.expected > 0 && (
                                <>
                                  {' · '}
                                  <span
                                    className={
                                      tRow.over ? 'text-rose-700'
                                      : tRow.balance === 0 ? 'text-emerald-700'
                                      : 'text-amber-700'
                                    }
                                    data-testid={`floor-recv-scan-group-balance-${g.item_number}`}
                                  >
                                    Balance <b>{tRow.over ? `+${tRow.scanned - tRow.expected}` : tRow.balance}</b>
                                  </span>
                                </>
                              )}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {g.rows.map((s) => (
                              <div key={s.id} className="flex items-center gap-2 text-[11px] bg-slate-50 rounded-md px-2 py-1.5" data-testid={`floor-recv-scan-${s.id}`}>
                                <span className="font-mono font-bold text-slate-700">{s.pallet_no}</span>
                                <span className="tabular-nums font-bold text-slate-900">{s.qty} {s.uom}</span>
                                {s.expiry_date && <span className="text-slate-500">· Exp {s.expiry_date}</span>}
                                {s.production_date && <span className="text-slate-500">· Prod {s.production_date}</span>}
                                <button
                                  type="button" onClick={() => undoScan(s.id)}
                                  className="ml-auto text-slate-400 hover:text-red-600 p-1"
                                  title="Undo scan" data-testid={`floor-recv-undo-${s.id}`}
                                  aria-label="Undo scan"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Feb 2026 — Full ASN Tally.
                  A single scrolling list of every SKU on the ASN
                  (plus any extras that were scanned) with the current
                  Expected / Scanned / Balance figures updating in
                  real time. Green pill = complete, amber pill = still
                  remaining, rose pill = over-scanned. Handheld
                  operators use this as their birds-eye "what's left"
                  view without having to remember every SKU. */}
              {tally.length > 0 && (
                <div className="border-t border-slate-100" data-testid="floor-receiving-full-tally">
                  <div className="px-4 py-2 bg-slate-50 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      Full ASN Tally
                    </div>
                    <div className="text-[10px] tabular-nums text-slate-600">
                      <span className="font-bold text-emerald-700" data-testid="floor-recv-tally-done-count">
                        {tally.filter((t) => !t.is_extra && t.expected > 0 && t.balance === 0 && !t.over).length}
                      </span>
                      <span className="text-slate-400"> / </span>
                      <span className="font-bold">{tally.filter((t) => !t.is_extra).length}</span>
                      <span className="text-slate-400"> lines complete</span>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {tally.map((t) => {
                      const isDone = !t.is_extra && t.expected > 0 && t.balance === 0 && !t.over;
                      const isOver = t.over;
                      const isExtra = t.is_extra;
                      const rowBg = isDone ? 'bg-emerald-50'
                                  : isOver ? 'bg-rose-50'
                                  : isExtra ? 'bg-slate-50'
                                  : '';
                      const pillClass = isDone ? 'bg-emerald-600 text-white'
                                      : isOver ? 'bg-rose-600 text-white'
                                      : isExtra ? 'bg-slate-500 text-white'
                                      : 'bg-amber-500 text-white';
                      const pillLabel = isDone ? 'DONE'
                                     : isOver ? `+${t.scanned - t.expected}`
                                     : isExtra ? 'EXTRA'
                                     : `${t.balance} left`;
                      return (
                        <div
                          key={t.item_number}
                          className={`px-4 py-2.5 flex items-center gap-3 ${rowBg}`}
                          data-testid={`floor-recv-tally-${t.item_number}`}
                          data-complete={isDone ? 'true' : 'false'}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {t.line_no != null && (
                                <span
                                  className="font-mono text-[10px] font-bold text-slate-400 tabular-nums"
                                  data-testid={`floor-recv-tally-lineno-${t.item_number}`}
                                >
                                  #{t.line_no}
                                </span>
                              )}
                              <span className="font-mono font-bold text-[#0078D4] text-xs">{t.item_number}</span>
                              <span className="text-[11px] text-slate-900 truncate">{t.product_name || '(unknown)'}</span>
                            </div>
                            <div className="mt-0.5 text-[10px] tabular-nums text-slate-600">
                              <span>Exp </span>
                              <span className="font-bold text-slate-900">{t.expected}</span>
                              <span className="text-slate-300"> · </span>
                              <span>Scan </span>
                              <span
                                className="font-bold text-slate-900"
                                data-testid={`floor-recv-tally-scanned-${t.item_number}`}
                              >
                                {t.scanned}
                              </span>
                              <span className="text-slate-300"> · </span>
                              <span>Bal </span>
                              <span
                                className={`font-bold ${isDone ? 'text-emerald-700' : isOver ? 'text-rose-700' : 'text-amber-700'}`}
                                data-testid={`floor-recv-tally-balance-${t.item_number}`}
                              >
                                {isOver ? 0 : t.balance}
                              </span>
                              <span className="text-slate-300"> · </span>
                              <span>{t.pallets} pallet{t.pallets === 1 ? '' : 's'}</span>
                            </div>
                          </div>
                          <div
                            className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider tabular-nums ${pillClass}`}
                            data-testid={`floor-recv-tally-pill-${t.item_number}`}
                          >
                            {pillLabel}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setAsn(null); setScan(''); setEntry({ item_number:'', qty:'', pallet_no:'', expiry_date:'', production_date:'' }); }}
                  className="h-11 px-4 rounded-full border border-slate-300 text-slate-700 bg-white hover:bg-slate-100 text-sm font-bold"
                  data-testid="floor-receiving-clear"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={completeReceipt}
                  disabled={completing || scans.length === 0}
                  className="h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold inline-flex items-center gap-2"
                  data-testid="floor-receiving-complete"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {completing ? 'Completing…' : `Complete Receipt (${scans.length})`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {!asn && !loading && (
        <div className="mt-8 text-center text-slate-500 text-sm" data-testid="floor-receiving-empty">
          Scan or type an ASN number to start receiving.
        </div>
      )}

      {/* Camera-scanner overlay — reused for ASN / SKU / Pallet fields */}
      <BarcodeScannerModal
        open={!!scanTarget}
        onClose={() => setScanTarget('')}
        onResult={(text) => {
          const val = String(text || '').trim();
          if (!val) return;
          if (scanTarget === 'asn') {
            setScan(val);
            setScanTarget('');
            // Auto-submit lookup — pass the value directly so we don't
            // race React's async state update.
            lookup(val);
          } else if (scanTarget === 'item') {
            setEntry((e0) => ({ ...e0, item_number: val }));
            setScanTarget('');
          } else if (scanTarget === 'pallet') {
            setEntry((e0) => ({ ...e0, pallet_no: val.toUpperCase() }));
            setScanTarget('');
          }
        }}
        helperText={
          scanTarget === 'asn' ? 'Point the camera at the ASN barcode'
          : scanTarget === 'item' ? 'Point the camera at the SKU barcode on the carton'
          : scanTarget === 'pallet' ? 'Point the camera at the pallet label barcode'
          : ''
        }
      />
    </div>
  );
};


const PalletReturnsFloorTab = ({ authHeaders }) => {  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [drafts, setDrafts] = useState({}); // { [return_id]: { received: string, notes: string } }

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pallets/floor-returns`, { headers: authHeaders });
      setRows(data?.returns || []);
      // Prefill each draft's received qty with the expected qty for
      // one-tap "matches expected" confirmation.
      const nextDrafts = {};
      (data?.returns || []).forEach((r) => {
        nextDrafts[r.id] = drafts[r.id] || { received: String(r.quantity), notes: '' };
      });
      setDrafts(nextDrafts);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const receive = async (r) => {
    const draft = drafts[r.id] || { received: '' };
    const n = parseInt(draft.received, 10);
    if (Number.isNaN(n) || n < 0) {
      toast({ title: 'Enter a non-negative received quantity' });
      return;
    }
    setBusyId(r.id);
    try {
      await axios.post(
        `${API}/admin/pallets/floor-returns/${r.id}/receive`,
        { received_qty: n, notes: (draft.notes || '').trim() },
        { headers: authHeaders },
      );
      toast({
        title: `Received ${n} pallets`,
        description: n !== r.quantity ? `Discrepancy vs expected (${r.quantity}) flagged.` : 'Warehouse inventory credited.',
      });
      await load();
    } catch (err) {
      toast({ title: 'Receive failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="px-3 pt-4 pb-24 space-y-3" data-testid="floor-pallet-returns-list">
      <div className="rounded-2xl bg-white/10 border border-white/20 p-3 text-white text-sm">
        <div className="font-bold uppercase tracking-wider text-xs text-white/80 mb-1">Pending pallet returns</div>
        <div className="text-white/70 text-xs leading-relaxed">
          Confirm each return as the pallets land at the dock. Entering fewer than expected flags a discrepancy for admin review.
        </div>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-12 text-center px-6" data-testid="floor-pallet-returns-empty">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <div className="text-white font-bold text-lg">No returns waiting</div>
          <p className="text-sm text-white/70 mt-1">Branches haven&apos;t sent any pallets back yet.</p>
        </div>
      ) : (
        rows.map((r) => {
          const draft = drafts[r.id] || { received: '', notes: '' };
          const declaredN = Number(r.quantity);
          const enteredN = parseInt(draft.received, 10);
          const willFlagDiscrepancy = !Number.isNaN(enteredN) && enteredN !== declaredN;
          return (
            <div
              key={r.id}
              className="rounded-2xl bg-white p-4 border border-slate-200 shadow-sm"
              data-testid={`floor-pallet-return-${r.id}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Branch return</div>
                  <div className="text-lg font-bold text-slate-900 truncate">{r.branch_name || '—'}</div>
                </div>
                <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
                  {r.pallet_type}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Order ref</div>
                  <div className="font-mono text-slate-800 truncate" data-testid={`floor-pallet-return-order-${r.id}`}>
                    {r.order_reference || '—'}
                  </div>
                </div>
                <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-2">
                  <div className="text-[10px] uppercase font-bold text-indigo-700">Expected qty</div>
                  <div className="text-lg font-bold text-indigo-900 tabular-nums" data-testid={`floor-pallet-return-expected-${r.id}`}>{declaredN}</div>
                </div>
              </div>
              {r.notes && (
                <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 mb-3">
                  Branch note: {r.notes}
                </div>
              )}
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Actual quantity received</label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={draft.received}
                onChange={(e) => setDrafts((p) => ({ ...p, [r.id]: { ...draft, received: e.target.value } }))}
                className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-emerald-400 focus:outline-none px-3 text-lg font-bold tabular-nums"
                data-testid={`floor-pallet-return-actual-${r.id}`}
              />
              {willFlagDiscrepancy && (
                <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid={`floor-pallet-return-discrepancy-${r.id}`}>
                  ⚠ You&apos;re about to record {enteredN} vs branch&apos;s {declaredN}. Add a note explaining the difference below.
                </div>
              )}
              <input
                value={draft.notes}
                onChange={(e) => setDrafts((p) => ({ ...p, [r.id]: { ...draft, notes: e.target.value } }))}
                placeholder="Notes (damage, shortage, condition...)"
                className="mt-2 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm"
                data-testid={`floor-pallet-return-notes-${r.id}`}
              />
              <button
                type="button"
                onClick={() => receive(r)}
                disabled={busyId === r.id || draft.received === ''}
                className="mt-3 h-12 w-full rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid={`floor-pallet-return-receive-${r.id}`}
              >
                {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                Receive pallets
              </button>
            </div>
          );
        })
      )}
    </div>
  );
};


export default FloorTasks;
